import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, Settings, LogOut, Plus, Pencil, Trash2, Search, 
  Download, ChevronRight, ChevronDown, Building2, UserPlus, Shield, Eye,
  GraduationCap, Goal, Bike, Waves, Timer, Dumbbell, Medal, Trophy,
  LayoutDashboard, FolderTree, UserCog, FileSpreadsheet, BarChart3,
  ArrowUpDown, Filter, MoreVertical, ImagePlus, Upload,
  Link2, CheckCircle2, XCircle, Clock, ExternalLink, Copy, Bell,
  DollarSign, Receipt, Percent, RefreshCw, FileText, Wallet,
  UserX, UserCheck,
  TrendingUp, TrendingDown, CreditCard, PiggyBank, BookOpen, Lock, AlertTriangle, ArrowLeft,
  Calendar, Play, Square, ClipboardList, MessageSquare, Save, Check, AlertCircle, Phone, Mail, History,
  LayoutGrid, List, Menu, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import FinanzasModuleAcademia from '@/components/academia/FinanzasModuleAcademia';
import ContabilidadModuleAcademia from '@/components/academia/ContabilidadModuleAcademia';
import SucursalesModule from '@/components/academia/SucursalesModule';
import UsuariosAcademiaModule from '@/components/academia/UsuariosAcademiaModule';
import GestionDashboard from '@/components/academia/GestionDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Días de la semana
const DIAS_SEMANA = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' }
];

// Disciplinas
const DISCIPLINAS = [
  { id: 'futbol', nombre: 'Fútbol', icon: Goal },
  { id: 'natacion', nombre: 'Natación', icon: Waves },
  { id: 'atletismo', nombre: 'Atletismo', icon: Timer },
  { id: 'ciclismo', nombre: 'Ciclismo', icon: Bike },
  { id: 'gimnasia', nombre: 'Gimnasia', icon: Dumbbell },
  { id: 'artes_marciales', nombre: 'Artes Marciales', icon: Medal },
  { id: 'tenis', nombre: 'Tenis', icon: Trophy },
  { id: 'basketball', nombre: 'Basketball', icon: Trophy },
  { id: 'volleyball', nombre: 'Volleyball', icon: Trophy },
  { id: 'otro', nombre: 'Otro', icon: GraduationCap }
];

const DISCIPLINA_ICONS = {
  futbol: Goal, natacion: Waves, atletismo: Timer, ciclismo: Bike,
  gimnasia: Dumbbell, artes_marciales: Medal, tenis: Trophy,
  basketball: Trophy, volleyball: Trophy, otro: GraduationCap
};

// Formatear RUT
const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let cuerpoFormateado = '';
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    contador++;
    if (contador === 3 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
      contador = 0;
    }
  }
  if (cuerpo.length === 0) return dv;
  return cuerpoFormateado + '-' + dv;
};

// Formatear dinero en pesos chilenos
const formatMoney = (amount) => {
  if (amount === null || amount === undefined) return '$0';
  return '$' + Math.round(amount).toLocaleString('es-CL');
};

// Validar RUT
const validarRut = (rut) => {
  if (!rut) return { valido: false, mensaje: 'RUT requerido' };
  const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 8) return { valido: false, mensaje: 'RUT muy corto' };
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = suma % 11;
  const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : String(11 - resto);
  if (dvIngresado === dvCalculado) return { valido: true, mensaje: 'RUT válido' };
  return { valido: false, mensaje: `DV incorrecto (debería ser ${dvCalculado})` };
};

// Copiar al portapapeles
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

export default function AcademiaDashboard() {
  const navigate = useNavigate();
  
  // Estados principales
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [academias, setAcademias] = useState([]);
  const [academiaActual, setAcademiaActual] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Datos de la academia
  const [categorias, setCategorias] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [entrenadores, setEntrenadores] = useState([]);
  const [clasesHoy, setClasesHoy] = useState([]);
  const [historialClases, setHistorialClases] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  
  // UI States
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [menuPlanesOpen, setMenuPlanesOpen] = useState(false);
  const [menuPagosOpen, setMenuPagosOpen] = useState(false);
  const [menuClasesOpen, setMenuClasesOpen] = useState(false);
  const [menuContabilidadOpen, setMenuContabilidadOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en móvil
  
  // Vista de lista o tarjetas (guardada en localStorage)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('kinosis_academia_view_mode') || 'cards';
  });
  
  // Guardar preferencia de vista en localStorage
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('kinosis_academia_view_mode', mode);
  };

  // Función para cambiar sección y cerrar sidebar en móvil
  const handleSetSection = (section) => {
    setActiveSection(section);
    setSidebarOpen(false); // Cerrar sidebar en móvil
  };
  
  // Dialogs
  const [showCrearAcademia, setShowCrearAcademia] = useState(false);
  const [showCrearCategoria, setShowCrearCategoria] = useState(false);
  const [showCrearAlumno, setShowCrearAlumno] = useState(false);
  const [showCrearEntrenador, setShowCrearEntrenador] = useState(false);
  const [showLinkInscripcion, setShowLinkInscripcion] = useState(false);
  const [showClaseModal, setShowClaseModal] = useState(false);
  const [showEditarAcademia, setShowEditarAcademia] = useState(false);
  const [showEditarCategoria, setShowEditarCategoria] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [showAgregarAlumnosCategoria, setShowAgregarAlumnosCategoria] = useState(false);
  const [searchTermCategoria, setSearchTermCategoria] = useState('');
  const [showEditarAlumno, setShowEditarAlumno] = useState(false);
  const [alumnoEditando, setAlumnoEditando] = useState(null);
  const [showSuspenderAlumno, setShowSuspenderAlumno] = useState(false);
  const [alumnoASuspender, setAlumnoASuspender] = useState(null);
  const [suspenderAlumnoForm, setSuspenderAlumnoForm] = useState({ motivo: '', detalle: '' });
  const [filtroEstadoAlumno, setFiltroEstadoAlumno] = useState('activo'); // activo, suspendido, todos
  
  // Estados para Cartola de Alumno
  const [showCartolaDialog, setShowCartolaDialog] = useState(false);
  const [cartolaAlumno, setCartolaAlumno] = useState(null);
  const [cartolaData, setCartolaData] = useState(null);
  const [loadingCartola, setLoadingCartola] = useState(false);
  const [cartolaPlanes, setCartolaPlanes] = useState([]);
  const [cartolaPagos, setCartolaPagos] = useState([]);
  const [cartolaFiltro, setCartolaFiltro] = useState('todos');
  const [cartolaTab, setCartolaTab] = useState('cuotas');
  
  // Filtros adicionales para otras secciones
  const [searchSucursal, setSearchSucursal] = useState('');
  const [searchCategoria, setSearchCategoria] = useState('');
  const [filtroSucursalCategoria, setFiltroSucursalCategoria] = useState('todas');
  const [searchEntrenador, setSearchEntrenador] = useState('');
  const [filtroEstadoEntrenador, setFiltroEstadoEntrenador] = useState('todos'); // todos, activo, pendiente
  
  const [showEditarDatosBancarios, setShowEditarDatosBancarios] = useState(false);
  const [datosBancariosForm, setDatosBancariosForm] = useState({
    banco: '', tipo_cuenta: 'corriente', numero_cuenta: '', rut_titular: '', nombre_titular: '', email_notificacion: ''
  });
  
  // Modal Upgrade a Pro
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeForm, setUpgradeForm] = useState({
    nombre: '', email: '', telefono: '', mensaje: ''
  });
  const [enviandoUpgrade, setEnviandoUpgrade] = useState(false);
  
  // Forms
  const [academiaForm, setAcademiaForm] = useState({
    nombre: '', disciplina: 'futbol', descripcion: '',
    email_contacto: '', telefono_contacto: '', direccion: '', pais: 'chile', logo: ''
  });
  const [categoriaForm, setCategoriaForm] = useState({
    nombre: '', descripcion: '', edad_min: '', edad_max: '', horarios: [], max_alumnos: '', sucursal_id: ''
  });
  const [alumnoForm, setAlumnoForm] = useState({
    nombre: '', apellido: '', documento_numero: '', fecha_nacimiento: '',
    email: '', telefono: '', es_su_propio_apoderado: false,
    apoderado: { nombre: '', apellido: '', telefono: '', email: '', parentesco: 'padre' }
  });
  const [entrenadorForm, setEntrenadorForm] = useState({
    nombre: '', apellido: '', email: '', telefono: '', especialidad: ''
  });
  const [horarioTemp, setHorarioTemp] = useState({ dia: 0, hora_inicio: '17:00', hora_fin: '18:00' });
  
  // Link inscripción
  const [linkInscripcion, setLinkInscripcion] = useState(null);
  const [categoriaParaLink, setCategoriaParaLink] = useState(null);
  
  // Clase activa
  const [claseActiva, setClaseActiva] = useState(null);
  const [asistenciasTemp, setAsistenciasTemp] = useState({});
  const [notaTemp, setNotaTemp] = useState('');
  const [tipoNotaTemp, setTipoNotaTemp] = useState('observacion');
  const [horaFinTemp, setHoraFinTemp] = useState('');
  const [showAgregarEntrenadorClase, setShowAgregarEntrenadorClase] = useState(false);
  
  // Filtros de clases
  const [filtroClasesCategoria, setFiltroClasesCategoria] = useState('');
  const [filtroClasesEntrenador, setFiltroClasesEntrenador] = useState('');
  const [filtroClasesFecha, setFiltroClasesFecha] = useState('');
  const [filtroClasesSucursal, setFiltroClasesSucursal] = useState('');
  const [clasesHistorial, setClasesHistorial] = useState([]);
  const [clasesAbiertas, setClasesAbiertas] = useState([]);
  
  const TIPOS_NOTA = [
    { value: 'observacion', label: 'Observación', color: 'bg-blue-600' },
    { value: 'avance', label: 'Avance', color: 'bg-emerald-600' },
    { value: 'incidente', label: 'Incidente', color: 'bg-red-600' },
    { value: 'actividad', label: 'Actividad Realizada', color: 'bg-purple-600' },
    { value: 'general', label: 'General', color: 'bg-slate-600' }
  ];
  
  // Solicitudes pendientes
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);

  // Rol del usuario en la academia actual
  const [userRolAcademia, setUserRolAcademia] = useState(null); // 'admin', 'super_usuario', 'lectura'

  // Plan y permisos
  const isPro = academiaActual?.plan === 'pro';
  // Admin y super_usuario pueden editar, lectura solo ve
  const isAdminAcademia = userRolAcademia === 'admin';
  const canEdit = userRolAcademia === 'admin' || userRolAcademia === 'super_usuario';

  // Manejar logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAcademiaForm({ ...academiaForm, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Cargar usuario
  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        navigate('/academias');
      }
    };
    loadUser();
  }, [navigate]);

  // Cargar academias
  useEffect(() => {
    if (user) loadAcademias();
  }, [user]);

  const loadAcademias = async () => {
    try {
      const res = await axios.get(`${API}/academias`, { withCredentials: true });
      const lista = res.data.academias || res.data || [];
      setAcademias(lista);
      if (lista.length > 0 && !academiaActual) {
        setAcademiaActual(lista[0]);
        loadAcademiaData(lista[0].id);
      }
    } catch (error) {
      console.error('Error loading academias:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAcademiaData = async (academiaId) => {
    try {
      const [catRes, alumRes, entRes, clasesRes, sucRes, rolRes] = await Promise.all([
        axios.get(`${API}/academias/${academiaId}/categorias`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/alumnos`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/entrenadores`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaId}/clases-hoy`, { withCredentials: true }).catch(() => ({ data: { clases_programadas: [] } })),
        axios.get(`${API}/academias/${academiaId}/sucursales`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/academias/${academiaId}/mi-rol`, { withCredentials: true }).catch(() => ({ data: { rol: 'lectura' } }))
      ]);
      
      setCategorias(catRes.data.categorias || catRes.data || []);
      const alumnosData = alumRes.data.alumnos || alumRes.data || [];
      setAlumnos(alumnosData);
      setEntrenadores(entRes.data.entrenadores || entRes.data || []);
      setClasesHoy(clasesRes.data.clases_programadas || []);
      setSucursales(sucRes.data || []);
      setUserRolAcademia(rolRes.data.rol || 'lectura');
      
      // Filtrar solicitudes pendientes
      setSolicitudesPendientes(alumnosData.filter(a => a.estado_inscripcion === 'pendiente'));
    } catch (error) {
      console.error('Error loading academia data:', error);
    }
  };

  // Función para recargar solo las sucursales
  const loadSucursales = async () => {
    if (!academiaActual) return;
    try {
      const res = await axios.get(`${API}/academias/${academiaActual.id}/sucursales`, { withCredentials: true });
      setSucursales(res.data || []);
    } catch (error) {
      console.error('Error loading sucursales:', error);
    }
  };

  // Recargar sucursales cuando cambiamos a la sección de categorías
  useEffect(() => {
    if (activeSection === 'categorias' && academiaActual) {
      loadSucursales();
    }
  }, [activeSection, academiaActual?.id]);

  const loadHistorialClases = async () => {
    if (!academiaActual) return;
    try {
      let url = `${API}/academias/${academiaActual.id}/clases-sesion?estado=cerrada`;
      const params = [];
      if (filtroClasesCategoria && filtroClasesCategoria !== '__all__') params.push(`categoria_id=${filtroClasesCategoria}`);
      if (filtroClasesEntrenador && filtroClasesEntrenador !== '__all__') params.push(`entrenador_id=${filtroClasesEntrenador}`);
      if (filtroClasesFecha) params.push(`fecha=${filtroClasesFecha}`);
      if (params.length > 0) url += '&' + params.join('&');
      
      const res = await axios.get(url, { withCredentials: true });
      setClasesHistorial(res.data.clases || []);
    } catch (error) {
      console.error('Error loading historial:', error);
    }
  };

  const loadClasesAbiertas = async () => {
    if (!academiaActual) return;
    try {
      let url = `${API}/academias/${academiaActual.id}/clases-sesion?estado=abierta`;
      if (filtroClasesCategoria && filtroClasesCategoria !== '__all__') url += `&categoria_id=${filtroClasesCategoria}`;
      if (filtroClasesEntrenador && filtroClasesEntrenador !== '__all__') url += `&entrenador_id=${filtroClasesEntrenador}`;
      
      const res = await axios.get(url, { withCredentials: true });
      setClasesAbiertas(res.data.clases || []);
    } catch (error) {
      console.error('Error loading clases abiertas:', error);
    }
  };

  // Cambiar academia
  const handleCambiarAcademia = (academia) => {
    setAcademiaActual(academia);
    loadAcademiaData(academia.id);
    setActiveSection('dashboard');
  };

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('session_token');
    navigate('/academias');
  };

  // Función de refresh con animación
  const handleRefresh = async () => {
    if (!academiaActual || refreshing) return;
    setRefreshing(true);
    await loadAcademiaData(academiaActual.id);
    setRefreshing(false);
  };

  // ==================== CRUD ACADEMIA ====================
  const handleCrearAcademia = async () => {
    if (!academiaForm.nombre) {
      toast.error('Nombre es requerido');
      return;
    }
    try {
      const slugAuto = academiaForm.nombre.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      const res = await axios.post(`${API}/academias`, { ...academiaForm, slug: slugAuto }, { withCredentials: true });
      toast.success('Academia creada');
      setShowCrearAcademia(false);
      setAcademiaForm({ nombre: '', disciplina: 'futbol', descripcion: '', email_contacto: '', telefono_contacto: '', direccion: '', pais: 'chile' });
      await loadAcademias();
      setAcademiaActual(res.data);
      loadAcademiaData(res.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear academia');
    }
  };

  const handleGuardarAcademia = async () => {
    try {
      const res = await axios.put(`${API}/academias/${academiaActual.id}`, academiaForm, { withCredentials: true });
      toast.success('Academia actualizada');
      setShowEditarAcademia(false);
      // Actualizar academia actual con los nuevos datos
      setAcademiaActual({ ...academiaActual, ...academiaForm });
      loadAcademias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleGuardarDatosBancarios = async () => {
    if (!datosBancariosForm.banco || !datosBancariosForm.numero_cuenta || !datosBancariosForm.rut_titular || !datosBancariosForm.nombre_titular) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    try {
      await axios.put(`${API}/academias/${academiaActual.id}`, {
        datos_bancarios: datosBancariosForm
      }, { withCredentials: true });
      toast.success('Datos bancarios guardados');
      setShowEditarDatosBancarios(false);
      setAcademiaActual({ ...academiaActual, datos_bancarios: datosBancariosForm });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // ==================== UPGRADE A PRO ====================
  const handleOpenUpgrade = () => {
    setUpgradeForm({
      nombre: user?.name || '',
      email: user?.email || '',
      telefono: '',
      mensaje: ''
    });
    setShowUpgradeModal(true);
  };

  const handleEnviarUpgrade = async () => {
    if (!upgradeForm.nombre || !upgradeForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    setEnviandoUpgrade(true);
    try {
      await axios.post(`${API}/contacto/upgrade`, {
        nombre: upgradeForm.nombre,
        email: upgradeForm.email,
        telefono: upgradeForm.telefono,
        mensaje: upgradeForm.mensaje,
        organizacion_id: academiaActual?.id,
        organizacion_nombre: academiaActual?.nombre,
        organizacion_tipo: 'academia',
        tipo: 'upgrade'
      });
      toast.success('¡Solicitud enviada! Te contactaremos pronto.');
      setShowUpgradeModal(false);
    } catch (error) {
      toast.error('Error al enviar la solicitud');
    } finally {
      setEnviandoUpgrade(false);
    }
  };

  // ==================== CRUD CATEGORÍAS ====================
  const handleCrearCategoria = async () => {
    if (!categoriaForm.sucursal_id) {
      toast.error('Debes seleccionar una sucursal');
      return;
    }
    if (!categoriaForm.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    // Verificar límite free
    if (!isPro && categorias.length >= 1) {
      toast.error('Plan Free: máximo 1 categoría. Actualiza a Pro para más.');
      return;
    }
    try {
      await axios.post(`${API}/academias/${academiaActual.id}/categorias`, {
        ...categoriaForm,
        edad_min: categoriaForm.edad_min ? parseInt(categoriaForm.edad_min) : null,
        edad_max: categoriaForm.edad_max ? parseInt(categoriaForm.edad_max) : null,
        max_alumnos: categoriaForm.max_alumnos ? parseInt(categoriaForm.max_alumnos) : null
      }, { withCredentials: true });
      toast.success('Categoría creada');
      setShowCrearCategoria(false);
      setCategoriaForm({ nombre: '', descripcion: '', edad_min: '', edad_max: '', horarios: [], max_alumnos: '', sucursal_id: '' });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear categoría');
    }
  };

  const agregarHorario = () => {
    setCategoriaForm({
      ...categoriaForm,
      horarios: [...categoriaForm.horarios, { ...horarioTemp }]
    });
    setHorarioTemp({ dia: 0, hora_inicio: '17:00', hora_fin: '18:00' });
  };

  const eliminarHorario = (index) => {
    setCategoriaForm({
      ...categoriaForm,
      horarios: categoriaForm.horarios.filter((_, i) => i !== index)
    });
  };

  const handleEditarCategoria = (categoria) => {
    setCategoriaEditando(categoria);
    setCategoriaForm({
      nombre: categoria.nombre || '',
      descripcion: categoria.descripcion || '',
      edad_min: categoria.edad_min || '',
      edad_max: categoria.edad_max || '',
      horarios: categoria.horarios || [],
      max_alumnos: categoria.max_alumnos || '',
      sucursal_id: categoria.sucursal_id || ''
    });
    setShowEditarCategoria(true);
  };

  const handleGuardarCategoria = async () => {
    if (!categoriaEditando) return;
    if (!categoriaForm.sucursal_id) {
      toast.error('Debes seleccionar una sucursal');
      return;
    }
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/categorias/${categoriaEditando.id}`, {
        ...categoriaForm,
        edad_min: categoriaForm.edad_min ? parseInt(categoriaForm.edad_min) : null,
        edad_max: categoriaForm.edad_max ? parseInt(categoriaForm.edad_max) : null,
        max_alumnos: categoriaForm.max_alumnos ? parseInt(categoriaForm.max_alumnos) : null
      }, { withCredentials: true });
      toast.success('Categoría actualizada');
      setShowEditarCategoria(false);
      setCategoriaEditando(null);
      setCategoriaForm({ nombre: '', descripcion: '', edad_min: '', edad_max: '', horarios: [], max_alumnos: '', sucursal_id: '' });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar categoría');
    }
  };

  const handleEliminarCategoria = async (categoriaId) => {
    if (!confirm('¿Eliminar esta categoría? Los alumnos asociados perderán esta asignación.')) return;
    try {
      await axios.delete(`${API}/academias/${academiaActual.id}/categorias/${categoriaId}`, { withCredentials: true });
      toast.success('Categoría eliminada');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  // Obtener nombre de sucursal
  const getSucursalNombre = (sucursalId) => {
    const suc = sucursales.find(s => s.id === sucursalId);
    return suc?.nombre || '';
  };

  // Obtener categorías filtradas por sucursal
  const getCategoriasPorSucursal = (sucursalId) => {
    return categorias.filter(c => c.sucursal_id === sucursalId);
  };

  // Filtrar categorías por sucursal seleccionada
  const categoriasFiltradas = filtroClasesSucursal && filtroClasesSucursal !== '__all__'
    ? categorias.filter(c => c.sucursal_id === filtroClasesSucursal)
    : categorias;

  // Obtener alumnos de una categoría
  const getAlumnosDeCategoria = (categoriaId) => {
    return alumnos.filter(a => a.categoria_ids?.includes(categoriaId) && a.estado !== 'rechazado');
  };

  // Obtener alumnos que NO están en una categoría
  const getAlumnosSinCategoria = (categoriaId) => {
    return alumnos.filter(a => !a.categoria_ids?.includes(categoriaId) && a.estado !== 'rechazado');
  };

  // Agregar/quitar alumno de una categoría
  const handleToggleAlumnoCategoria = async (alumno, categoriaId) => {
    const tieneCategoria = alumno.categoria_ids?.includes(categoriaId);
    const nuevasCategorias = tieneCategoria
      ? alumno.categoria_ids.filter(id => id !== categoriaId)
      : [...(alumno.categoria_ids || []), categoriaId];
    
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/alumnos/${alumno.id}`, {
        categoria_ids: nuevasCategorias
      }, { withCredentials: true });
      toast.success(tieneCategoria ? 'Alumno removido de la categoría' : 'Alumno agregado a la categoría');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al actualizar alumno');
    }
  };

  // Editar alumno
  const handleEditarAlumno = (alumno) => {
    setAlumnoEditando(alumno);
    setAlumnoForm({
      nombre: alumno.nombre || '',
      apellido: alumno.apellido || '',
      documento_numero: alumno.documento_numero || '',
      fecha_nacimiento: alumno.fecha_nacimiento || '',
      email: alumno.email || '',
      telefono: alumno.telefono || '',
      es_su_propio_apoderado: alumno.es_su_propio_apoderado || false,
      apoderado: alumno.apoderado || { nombre: '', apellido: '', telefono: '', email: '', parentesco: 'padre' },
      categoria_ids: alumno.categoria_ids || []
    });
    setShowEditarAlumno(true);
  };

  const handleGuardarAlumno = async () => {
    if (!alumnoEditando) return;
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/alumnos/${alumnoEditando.id}`, alumnoForm, { withCredentials: true });
      toast.success('Alumno actualizado');
      setShowEditarAlumno(false);
      setAlumnoEditando(null);
      setAlumnoForm({
        nombre: '', apellido: '', documento_numero: '', fecha_nacimiento: '',
        email: '', telefono: '', es_su_propio_apoderado: false,
        apoderado: { nombre: '', apellido: '', telefono: '', email: '', parentesco: 'padre' }
      });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar alumno');
    }
  };

  // ==================== CRUD ALUMNOS ====================
  const handleCrearAlumno = async () => {
    if (!alumnoForm.nombre || !alumnoForm.apellido) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }
    if (!alumnoForm.documento_numero) {
      toast.error('El RUT es obligatorio');
      return;
    }
    if (!alumnoForm.email) {
      toast.error('El email es obligatorio');
      return;
    }
    // Verificar límite free
    const alumnosActivos = alumnos.filter(a => a.estado !== 'rechazado' && a.estado !== 'inactivo').length;
    if (!isPro && alumnosActivos >= 20) {
      toast.error('Plan Free: máximo 20 alumnos. Actualiza a Pro para más.');
      return;
    }
    try {
      await axios.post(`${API}/academias/${academiaActual.id}/alumnos`, {
        ...alumnoForm,
        categoria_ids: categoriaFiltro !== 'todas' ? [categoriaFiltro] : []
      }, { withCredentials: true });
      toast.success('Alumno creado');
      setShowCrearAlumno(false);
      setAlumnoForm({
        nombre: '', apellido: '', documento_numero: '', fecha_nacimiento: '',
        email: '', telefono: '', es_su_propio_apoderado: false,
        apoderado: { nombre: '', apellido: '', telefono: '', email: '', parentesco: 'padre' }
      });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear alumno');
    }
  };

  const handleEliminarAlumno = async (id) => {
    if (!confirm('¿Eliminar este alumno?')) return;
    try {
      await axios.delete(`${API}/academias/${academiaActual.id}/alumnos/${id}`, { withCredentials: true });
      toast.success('Alumno eliminado');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Suspender alumno
  const handleSuspenderAlumno = async () => {
    if (!alumnoASuspender || !suspenderAlumnoForm.motivo) {
      toast.error('Selecciona un motivo');
      return;
    }
    try {
      await axios.put(
        `${API}/academias/${academiaActual.id}/alumnos/${alumnoASuspender.id}/suspender`,
        suspenderAlumnoForm,
        { withCredentials: true }
      );
      toast.success('Alumno suspendido');
      setShowSuspenderAlumno(false);
      setAlumnoASuspender(null);
      setSuspenderAlumnoForm({ motivo: '', detalle: '' });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suspender');
    }
  };

  // Reactivar alumno
  const handleReactivarAlumno = async (alumno) => {
    if (!confirm(`¿Reactivar a ${alumno.nombre} ${alumno.apellido}?`)) return;
    try {
      await axios.put(
        `${API}/academias/${academiaActual.id}/alumnos/${alumno.id}/reactivar`,
        {},
        { withCredentials: true }
      );
      toast.success('Alumno reactivado');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reactivar');
    }
  };

  // Aprobar/Rechazar inscripción
  const handleAprobarInscripcion = async (alumnoId) => {
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/alumnos/${alumnoId}`, 
        { estado_inscripcion: 'aprobado', estado: 'activo' }, 
        { withCredentials: true }
      );
      toast.success('Inscripción aprobada');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleRechazarInscripcion = async (alumnoId) => {
    if (!confirm('¿Rechazar esta solicitud?')) return;
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/alumnos/${alumnoId}`, 
        { estado_inscripcion: 'rechazado', estado: 'rechazado' }, 
        { withCredentials: true }
      );
      toast.success('Inscripción rechazada');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  // Ver cartola del alumno
  const handleVerCartolaAlumno = async (alumno) => {
    setCartolaAlumno(alumno);
    setShowCartolaDialog(true);
    setLoadingCartola(true);
    setCartolaFiltro('todos');
    setCartolaTab('cuotas');
    try {
      const [cuotasRes, planesRes, pagosRes] = await Promise.all([
        axios.get(`${API}/academias/${academiaActual.id}/alumnos/${alumno.id}/cuotas`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaActual.id}/alumnos/${alumno.id}/planes`, { withCredentials: true }),
        axios.get(`${API}/academias/${academiaActual.id}/alumnos/${alumno.id}/pagos`, { withCredentials: true })
      ]);
      setCartolaData(cuotasRes.data);
      setCartolaPlanes(planesRes.data || []);
      setCartolaPagos(pagosRes.data || []);
    } catch (error) {
      console.error('Error loading cartola:', error);
      toast.error('Error al cargar la cartola');
    } finally {
      setLoadingCartola(false);
    }
  };

  // Recalcular cuotas del alumno
  const handleRecalcularCuotasAlumno = async () => {
    if (!cartolaAlumno) return;
    try {
      await axios.post(`${API}/academias/${academiaActual.id}/alumnos/${cartolaAlumno.id}/recalcular-cuotas`, {}, { withCredentials: true });
      toast.success('Cuotas recalculadas');
      handleVerCartolaAlumno(cartolaAlumno);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular');
    }
  };

  // Quitar plan de alumno
  const handleQuitarPlanAlumno = async (planId) => {
    if (!window.confirm('¿Quitar este plan del alumno? Las cuotas pendientes serán eliminadas.')) return;
    try {
      await axios.delete(`${API}/academias/${academiaActual.id}/alumnos/${cartolaAlumno.id}/plan/${planId}`, { withCredentials: true });
      toast.success('Plan removido');
      handleVerCartolaAlumno(cartolaAlumno);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al quitar plan');
    }
  };

  // Condonar cuota de alumno
  const handleCondonarCuotaAlumno = async (cuotaId) => {
    if (!window.confirm('¿Condonar esta cuota? Se marcará como pagada sin registro de pago.')) return;
    try {
      await axios.post(`${API}/academias/${academiaActual.id}/cuotas/${cuotaId}/condonar`, {}, { withCredentials: true });
      toast.success('Cuota condonada');
      handleVerCartolaAlumno(cartolaAlumno);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al condonar');
    }
  };

  // Anular cuota de alumno
  const handleAnularCuotaAlumno = async (cuotaId) => {
    if (!window.confirm('¿Anular esta cuota? Se eliminará del sistema.')) return;
    try {
      await axios.delete(`${API}/academias/${academiaActual.id}/cuotas/${cuotaId}`, { withCredentials: true });
      toast.success('Cuota anulada');
      handleVerCartolaAlumno(cartolaAlumno);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular');
    }
  };

  // Filtrar cuotas de la cartola
  const cuotasFiltradas = cartolaData?.cuotas?.filter(c => {
    if (cartolaFiltro === 'todos') return true;
    return c.estado === cartolaFiltro;
  }) || [];

  // ==================== CRUD ENTRENADORES ====================
  const handleCrearEntrenador = async () => {
    if (!entrenadorForm.nombre || !entrenadorForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    // Verificar límite free
    if (!isPro && entrenadores.length >= 1) {
      toast.error('Plan Free: máximo 1 entrenador. Actualiza a Pro para más.');
      return;
    }
    try {
      const res = await axios.post(`${API}/academias/${academiaActual.id}/entrenadores`, entrenadorForm, { withCredentials: true });
      if (res.data.email_enviado) {
        toast.success('Entrenador creado. Invitación enviada por email.');
      } else {
        toast.success('Entrenador creado. Reenvía la invitación manualmente.');
      }
      setShowCrearEntrenador(false);
      setEntrenadorForm({ nombre: '', apellido: '', email: '', telefono: '', especialidad: '' });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear entrenador');
    }
  };

  const handleReenviarInvitacion = async (entrenador) => {
    try {
      const res = await axios.post(
        `${API}/academias/${academiaActual.id}/entrenadores/${entrenador.id}/reenviar-invitacion`,
        {},
        { withCredentials: true }
      );
      if (res.data.email_enviado) {
        toast.success('Invitación reenviada por email');
      } else {
        // Mostrar el link para copiar manualmente
        await copyToClipboard(res.data.invitacion_url);
        toast.success('Link copiado al portapapeles (email no configurado)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar invitación');
    }
  };

  const handleEliminarEntrenador = async (entrenadorId) => {
    if (!confirm('¿Eliminar este entrenador?')) return;
    try {
      await axios.delete(`${API}/academias/${academiaActual.id}/entrenadores/${entrenadorId}`, { withCredentials: true });
      toast.success('Entrenador eliminado');
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const [showEditarEntrenador, setShowEditarEntrenador] = useState(false);
  const [entrenadorEditando, setEntrenadorEditando] = useState(null);
  const [showCambiarPassword, setShowCambiarPassword] = useState(false);
  const [nuevaPasswordEntrenador, setNuevaPasswordEntrenador] = useState('');
  const [confirmarPasswordEntrenador, setConfirmarPasswordEntrenador] = useState('');

  const handleEditarEntrenador = (entrenador) => {
    setEntrenadorEditando(entrenador);
    setEntrenadorForm({
      nombre: entrenador.nombre || '',
      apellido: entrenador.apellido || '',
      email: entrenador.email || '',
      telefono: entrenador.telefono || '',
      especialidad: entrenador.especialidad || ''
    });
    setNuevaPasswordEntrenador('');
    setConfirmarPasswordEntrenador('');
    setShowCambiarPassword(false);
    setShowEditarEntrenador(true);
  };

  const handleGuardarEntrenador = async () => {
    if (!entrenadorEditando) return;
    try {
      await axios.put(
        `${API}/academias/${academiaActual.id}/entrenadores/${entrenadorEditando.id}`,
        entrenadorForm,
        { withCredentials: true }
      );
      toast.success('Entrenador actualizado');
      setShowEditarEntrenador(false);
      setEntrenadorEditando(null);
      setEntrenadorForm({ nombre: '', apellido: '', email: '', telefono: '', especialidad: '' });
      loadAcademiaData(academiaActual.id);
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleCambiarPasswordEntrenador = async () => {
    if (!entrenadorEditando || !nuevaPasswordEntrenador) return;
    if (nuevaPasswordEntrenador.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (nuevaPasswordEntrenador !== confirmarPasswordEntrenador) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    try {
      await axios.post(
        `${API}/academias/${academiaActual.id}/entrenadores/${entrenadorEditando.id}/cambiar-password`,
        { password: nuevaPasswordEntrenador },
        { withCredentials: true }
      );
      toast.success('Contraseña actualizada correctamente');
      setNuevaPasswordEntrenador('');
      setConfirmarPasswordEntrenador('');
      setShowCambiarPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const handleDiagnosticarEntrenador = async (entrenador) => {
    try {
      const res = await axios.get(
        `${API}/academias/${academiaActual.id}/entrenadores/${entrenador.id}/debug-usuario`,
        { withCredentials: true }
      );
      const data = res.data;
      console.log('Debug entrenador:', data);
      
      let mensaje = `📋 DIAGNÓSTICO DEL ENTRENADOR\n\n`;
      mensaje += `Email del entrenador: ${data.entrenador?.email}\n`;
      mensaje += `Invitación aceptada: ${data.entrenador?.invitacion_aceptada ? 'Sí' : 'No'}\n\n`;
      
      mensaje += `👤 USUARIO (por email):\n`;
      mensaje += `  - Encontrado: ${data.usuario_por_email?.encontrado ? 'Sí' : 'NO ❌'}\n`;
      if (data.usuario_por_email?.encontrado) {
        mensaje += `  - Email verificado: ${data.usuario_por_email?.email_verified ? 'Sí' : 'NO ❌'}\n`;
        mensaje += `  - Tiene contraseña: ${data.usuario_por_email?.tiene_password ? 'Sí' : 'NO ❌'}\n`;
      }
      
      alert(mensaje);
    } catch (error) {
      toast.error('Error al diagnosticar: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ==================== LINK INSCRIPCIÓN ====================
  const handleGenerarLinkInscripcion = async (categoria) => {
    try {
      const res = await axios.post(
        `${API}/academias/${academiaActual.id}/categorias/${categoria.id}/link-inscripcion`,
        {},
        { withCredentials: true }
      );
      setLinkInscripcion(res.data.url);
      setCategoriaParaLink(categoria);
      setShowLinkInscripcion(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar link');
    }
  };

  const copiarLink = async () => {
    const copied = await copyToClipboard(linkInscripcion);
    if (copied) toast.success('Link copiado al portapapeles');
  };

  // ==================== CLASES ====================
  const handleIniciarClase = async (categoriaId) => {
    try {
      const res = await axios.post(`${API}/academias/${academiaActual.id}/clases-sesion`, {
        categoria_id: categoriaId
      }, { withCredentials: true });
      toast.success('Clase iniciada');
      loadAcademiaData(academiaActual.id);
      handleAbrirClase(res.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar clase');
    }
  };

  const handleAbrirClase = async (claseId) => {
    try {
      const res = await axios.get(`${API}/academias/${academiaActual.id}/clases-sesion/${claseId}`, { withCredentials: true });
      setClaseActiva(res.data);
      const asistInit = {};
      res.data.alumnos?.forEach(al => {
        asistInit[al.id] = al.presente !== null ? al.presente : true;
      });
      setAsistenciasTemp(asistInit);
      setShowClaseModal(true);
    } catch (error) {
      toast.error('Error al cargar clase');
    }
  };

  const handleCerrarClase = async () => {
    if (!claseActiva) return;
    try {
      await axios.put(`${API}/academias/${academiaActual.id}/clases-sesion/${claseActiva.id}/cerrar`, {}, { withCredentials: true });
      toast.success('Clase cerrada');
      setShowClaseModal(false);
      setClaseActiva(null);
      loadAcademiaData(academiaActual.id);
      loadHistorialClases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar clase');
    }
  };

  const handleGuardarAsistencia = async () => {
    if (!claseActiva) return;
    try {
      const asistencias = Object.entries(asistenciasTemp).map(([alumno_id, presente]) => ({
        alumno_id, presente
      }));
      await axios.post(`${API}/academias/${academiaActual.id}/clases-sesion/${claseActiva.id}/asistencia-multiple`, {
        asistencias
      }, { withCredentials: true });
      toast.success('Asistencia guardada');
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      toast.error('Error al guardar asistencia');
    }
  };

  const handleAgregarNota = async () => {
    if (!claseActiva || !notaTemp.trim()) return;
    try {
      await axios.post(`${API}/academias/${academiaActual.id}/clases-sesion/${claseActiva.id}/notas`, {
        contenido: notaTemp,
        tipo: 'general'
      }, { withCredentials: true });
      toast.success('Nota agregada');
      setNotaTemp('');
      handleAbrirClase(claseActiva.id);
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  // Cargar historial/clases abiertas cuando se selecciona esa sección
  useEffect(() => {
    if (activeSection === 'clases-historial' && academiaActual) {
      loadHistorialClases();
    }
    if (activeSection === 'clases-hoy' && academiaActual) {
      loadClasesAbiertas();
    }
  }, [activeSection, academiaActual, filtroClasesCategoria, filtroClasesEntrenador, filtroClasesFecha]);

  // Filtrar alumnos
  // Contadores de alumnos por estado
  const alumnosActivos = alumnos.filter(a => a.estado_inscripcion !== 'pendiente' && (a.estado || 'activo') === 'activo').length;
  const alumnosSuspendidos = alumnos.filter(a => a.estado === 'suspendido').length;

  const alumnosFiltrados = alumnos
    .filter(a => a.estado_inscripcion !== 'pendiente' && a.estado_inscripcion !== 'rechazado')
    .filter(a => {
      // Filtro por estado
      const estadoAlumno = a.estado || 'activo';
      if (filtroEstadoAlumno === 'activo') return estadoAlumno === 'activo';
      if (filtroEstadoAlumno === 'suspendido') return estadoAlumno === 'suspendido';
      return true; // todos
    })
    .filter(a => {
      if (categoriaFiltro === 'todas') return true;
      return a.categoria_ids?.includes(categoriaFiltro);
    })
    .filter(a => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return `${a.nombre} ${a.apellido}`.toLowerCase().includes(term) ||
             (a.documento_numero && a.documento_numero.toLowerCase().includes(term));
    });

  const DisciplinaIcon = academiaActual?.disciplina ? (DISCIPLINA_ICONS[academiaActual.disciplina] || GraduationCap) : GraduationCap;

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <GraduationCap className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // ==================== SIN ACADEMIAS ====================
  if (academias.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Card className="p-8 bg-slate-800 border-slate-700 text-center">
            <GraduationCap className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Bienvenido a Academias</h1>
            <p className="text-slate-400 mb-6">Crea tu primera academia para comenzar a gestionar alumnos, categorías y clases.</p>
            <Button onClick={() => setShowCrearAcademia(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Crear Academia
            </Button>
          </Card>
          
          <Button variant="ghost" onClick={() => navigate('/')} className="w-full mt-4 text-slate-400">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
          </Button>
        </div>

        {/* Modal Crear Academia */}
        <Dialog open={showCrearAcademia} onOpenChange={setShowCrearAcademia}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Crear Nueva Academia</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre de la Academia *</Label>
                <Input
                  value={academiaForm.nombre}
                  onChange={(e) => setAcademiaForm({ ...academiaForm, nombre: e.target.value })}
                  placeholder="Ej: Academia de Karate Tigres"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Disciplina</Label>
                <Select value={academiaForm.disciplina} onValueChange={(v) => setAcademiaForm({ ...academiaForm, disciplina: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {DISCIPLINAS.map(d => (
                      <SelectItem key={d.id} value={d.id} className="text-white hover:bg-slate-600">{d.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={academiaForm.telefono_contacto}
                    onChange={(e) => setAcademiaForm({ ...academiaForm, telefono_contacto: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={academiaForm.email_contacto}
                    onChange={(e) => setAcademiaForm({ ...academiaForm, email_contacto: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <Button onClick={handleCrearAcademia} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Crear Academia
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==================== MAIN LAYOUT ====================
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Top Bar */}
      <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          {/* Botón hamburguesa - visible solo en móvil */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src="/kinosis-logo.png" alt="Kinosis" className="h-8 sm:h-10 w-auto" />
        </div>
        
        <div className="flex items-center gap-4">
          {/* Notificación de solicitudes pendientes */}
          {solicitudesPendientes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveSection('solicitudes')}
              className="relative text-amber-400 hover:text-amber-300"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                {solicitudesPendientes.length}
              </span>
            </Button>
          )}
          
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.charAt(0) || user?.email?.charAt(0)}
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-white">{user?.name || user?.email}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-700">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Salir</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Overlay para cerrar el menú en móvil */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside className={`
          fixed lg:relative inset-y-0 left-0 z-30
          w-64 bg-slate-800 border-r border-slate-700 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none lg:shrink-0
          pt-16 lg:pt-0
        `}>
          {/* Academia Selector */}
          {academias.length > 1 && (
            <div className="p-3 border-b border-slate-700">
              <Select value={academiaActual.id} onValueChange={(id) => handleCambiarAcademia(academias.find(a => a.id === id))}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <div className="flex items-center gap-2 truncate">
                    <GraduationCap className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="truncate">{academiaActual.nombre}</span>
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {academias.map(academia => (
                    <SelectItem key={academia.id} value={academia.id} className="text-white">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-emerald-400" />
                        <span>{academia.nombre}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Academia Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              {academiaActual.logo ? (
                <img src={academiaActual.logo} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <DisciplinaIcon className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-white truncate">{academiaActual.nombre}</h2>
                <p className="text-xs text-emerald-400">
                  {DISCIPLINAS.find(d => d.id === academiaActual.disciplina)?.nombre || 'Academia'}
                </p>
              </div>
            </div>
            <Badge className={`mt-2 ${isPro ? 'bg-emerald-600' : 'bg-slate-600'}`}>
              {isPro ? 'Plan Pro' : 'Plan Free'}
            </Badge>
            {/* Mostrar rol del usuario */}
            {userRolAcademia && userRolAcademia !== 'admin' && (
              <Badge className={`mt-1 ml-1 ${userRolAcademia === 'super_usuario' ? 'bg-blue-600' : 'bg-amber-600'}`}>
                {userRolAcademia === 'super_usuario' ? 'Super Usuario' : 'Solo Lectura'}
              </Badge>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            <button
              onClick={() => handleSetSection('dashboard')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'dashboard' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Inicio</span>
            </button>
            
            {/* Sucursales - Primero */}
            <button
              onClick={() => handleSetSection('sucursales')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'sucursales' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <Building2 className="w-5 h-5" />
              <span>Sucursales</span>
              <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{sucursales.length}</span>
            </button>

            {/* Categorías - Segundo */}
            <button
              onClick={() => handleSetSection('categorias')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'categorias' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <FolderTree className="w-5 h-5" />
              <span>Categorías</span>
              <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{categorias.length}</span>
            </button>
            
            {/* Profesores (Entrenadores) - Tercero */}
            <button
              onClick={() => handleSetSection('entrenadores')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'entrenadores' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <UserCog className="w-5 h-5" />
              <span>Entrenadores</span>
              <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{entrenadores.length}</span>
            </button>

            {/* Alumnos - Cuarto */}
            <button
              onClick={() => handleSetSection('alumnos')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'alumnos' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <Users className="w-5 h-5" />
              <span>Alumnos</span>
              <span className="ml-auto text-xs bg-slate-700 px-2 py-0.5 rounded-full">{alumnosFiltrados.length}</span>
            </button>

            {/* Clases Menu */}
            <button
              onClick={() => setMenuClasesOpen(!menuClasesOpen)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('clases') ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <Calendar className="w-5 h-5" />
              <span>Clases</span>
              <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuClasesOpen ? 'rotate-180' : ''}`} />
            </button>
            {menuClasesOpen && (
              <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                <button
                  onClick={() => handleSetSection('clases-hoy')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'clases-hoy' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <Play className="w-4 h-4" />
                  <span>Clases de Hoy</span>
                  {clasesHoy.length > 0 && (
                    <span className="ml-auto text-xs bg-amber-600 px-2 py-0.5 rounded-full">{clasesHoy.length}</span>
                  )}
                </button>
                <button
                  onClick={() => handleSetSection('clases-historial')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'clases-historial' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <History className="w-4 h-4" />
                  <span>Historial</span>
                </button>
              </div>
            )}

            {/* Solicitudes Pendientes */}
            {solicitudesPendientes.length > 0 && (
              <button
                onClick={() => handleSetSection('solicitudes')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'solicitudes' ? 'bg-amber-600 text-white' : 'text-amber-400 hover:bg-slate-700'}`}
              >
                <Bell className="w-5 h-5" />
                <span>Solicitudes</span>
                <span className="ml-auto text-xs bg-red-500 px-2 py-0.5 rounded-full text-white">{solicitudesPendientes.length}</span>
              </button>
            )}

            {/* Planes Menu - Solo Pro */}
            {isPro && canEdit && (
              <>
                <button
                  onClick={() => setMenuPlanesOpen(!menuPlanesOpen)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('planes') ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <FileText className="w-5 h-5" />
                  <span>Planes</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuPlanesOpen ? 'rotate-180' : ''}`} />
                </button>
                {menuPlanesOpen && (
                  <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                    <button
                      onClick={() => handleSetSection('planes-cuotas')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-cuotas' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>Planes de Cuotas</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('planes-asignar-individual')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-asignar-individual' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Asignar Individual</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('planes-asignar-masivo')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'planes-asignar-masivo' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <Users className="w-4 h-4" />
                      <span>Asignar Masivo</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Pagos Menu - Solo Pro */}
            {isPro && canEdit && (
              <>
                <button
                  onClick={() => setMenuPagosOpen(!menuPagosOpen)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('pagos') ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <Wallet className="w-5 h-5" />
                  <span>Pagos</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuPagosOpen ? 'rotate-180' : ''}`} />
                </button>
                {menuPagosOpen && (
                  <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                    <button
                      onClick={() => handleSetSection('pagos-ingresos')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-ingresos' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Registrar Ingreso</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('pagos-historial')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-historial' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <History className="w-4 h-4" />
                      <span>Historial de Pagos</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('pagos-cargo')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-cargo' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <Receipt className="w-4 h-4" />
                      <span>Crear Cargo</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('pagos-excepcion')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'pagos-excepcion' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <Percent className="w-4 h-4" />
                      <span>Crear Excepción</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Gestión / Analytics */}
            <button
              onClick={() => handleSetSection('gestion')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'gestion' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
            >
              <BarChart3 className="w-5 h-5" />
              <span>Gestión</span>
            </button>

            {/* Finanzas y Contabilidad - Solo Pro */}
            {isPro && canEdit && (
              <>
                <button
                  onClick={() => handleSetSection('finanzas')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'finanzas' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <TrendingUp className="w-5 h-5" />
                  <span>Finanzas</span>
                </button>
                <button
                  onClick={() => setMenuContabilidadOpen(!menuContabilidadOpen)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection.startsWith('contabilidad-') ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Contabilidad</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${menuContabilidadOpen ? 'rotate-180' : ''}`} />
                </button>
                {menuContabilidadOpen && (
                  <div className="ml-4 pl-4 border-l border-slate-600 space-y-1">
                    <button
                      onClick={() => handleSetSection('contabilidad-ingresos')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-ingresos' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>Ingresos</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('contabilidad-egresos')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-egresos' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <TrendingDown className="w-4 h-4" />
                      <span>Egresos</span>
                    </button>
                    <button
                      onClick={() => handleSetSection('contabilidad-cuentas')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === 'contabilidad-cuentas' ? 'bg-emerald-600/50 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>Cuentas por Pagar</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Usuarios de Academia - Solo admin puede gestionar */}
            {isAdminAcademia && (
              <button
                onClick={() => handleSetSection('usuarios')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'usuarios' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                <Shield className="w-5 h-5" />
                <span>Usuarios</span>
              </button>
            )}

            {/* Configuración - Solo admin y super_usuario */}
            {canEdit && (
              <button
                onClick={() => handleSetSection('configuracion')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeSection === 'configuracion' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}
              >
                <Settings className="w-5 h-5" />
                <span>Configuración</span>
              </button>
            )}
          </nav>

          {/* Plan Free Banner */}
          {!isPro && (
            <div className="p-3 border-t border-slate-700">
              <Card className="p-3 bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border-emerald-700">
                <p className="text-xs text-emerald-300 font-medium">Plan Free</p>
                <p className="text-xs text-slate-400 mt-1">
                  {categorias.length}/1 categoría • {alumnos.filter(a => a.estado !== 'rechazado').length}/20 alumnos
                </p>
                <Button size="sm" className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={handleOpenUpgrade}>
                  Actualizar a Pro
                </Button>
              </Card>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Dashboard */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-white">Dashboard</h1>
              
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-emerald-600/20 rounded-lg">
                      <Users className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{alumnosFiltrados.length}</p>
                      <p className="text-sm text-slate-400">Alumnos</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <FolderTree className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{categorias.length}</p>
                      <p className="text-sm text-slate-400">Categorías</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-600/20 rounded-lg">
                      <UserCog className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{entrenadores.length}</p>
                      <p className="text-sm text-slate-400">Entrenadores</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-600/20 rounded-lg">
                      <Calendar className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{clasesHoy.length}</p>
                      <p className="text-sm text-slate-400">Clases Hoy</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Solicitudes Pendientes */}
              {solicitudesPendientes.length > 0 && (
                <Card className="p-4 bg-amber-900/20 border-amber-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-amber-400 flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Solicitudes de Inscripción Pendientes
                    </h3>
                    <Button size="sm" variant="outline" onClick={() => setActiveSection('solicitudes')} className="border-amber-600 text-amber-400">
                      Ver todas
                    </Button>
                  </div>
                  <p className="text-slate-300">Tienes {solicitudesPendientes.length} solicitudes esperando aprobación.</p>
                </Card>
              )}

              {/* Clases de Hoy */}
              {clasesHoy.length > 0 && (
                <Card className="p-4 bg-slate-800 border-slate-700">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-400" />
                    Clases Programadas para Hoy
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {clasesHoy.map((clase, idx) => (
                      <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-white">{clase.categoria_nombre}</p>
                            <p className="text-sm text-slate-400">{clase.horario?.hora_inicio} - {clase.horario?.hora_fin}</p>
                          </div>
                          <Badge className={clase.clase_abierta?.estado === 'abierta' ? 'bg-emerald-600' : clase.clase_abierta?.estado === 'cerrada' ? 'bg-slate-600' : 'bg-amber-600'}>
                            {clase.clase_abierta?.estado === 'abierta' ? 'En curso' : clase.clase_abierta?.estado === 'cerrada' ? 'Finalizada' : 'Pendiente'}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{clase.alumnos_count} alumnos</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Accesos Rápidos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 bg-slate-800 border-slate-700 hover:bg-slate-750 cursor-pointer transition-colors" onClick={() => setShowCrearAlumno(true)}>
                  <UserPlus className="w-8 h-8 text-emerald-400 mb-2" />
                  <h3 className="font-semibold text-white">Nuevo Alumno</h3>
                  <p className="text-sm text-slate-400">Inscribir alumno manualmente</p>
                </Card>
                <Card className="p-4 bg-slate-800 border-slate-700 hover:bg-slate-750 cursor-pointer transition-colors" onClick={() => setActiveSection('clases-hoy')}>
                  <Play className="w-8 h-8 text-amber-400 mb-2" />
                  <h3 className="font-semibold text-white">Iniciar Clase</h3>
                  <p className="text-sm text-slate-400">Abrir clase y pasar asistencia</p>
                </Card>
                <Card className="p-4 bg-slate-800 border-slate-700 hover:bg-slate-750 cursor-pointer transition-colors" onClick={() => setActiveSection('categorias')}>
                  <Link2 className="w-8 h-8 text-blue-400 mb-2" />
                  <h3 className="font-semibold text-white">Link de Inscripción</h3>
                  <p className="text-sm text-slate-400">Generar link para nuevos alumnos</p>
                </Card>
              </div>
            </div>
          )}

          {/* Alumnos */}
          {activeSection === 'alumnos' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-white">Alumnos</h1>
                <div className="flex items-center gap-3">
                  {/* Toggle de vista */}
                  <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                    <button
                      onClick={() => toggleViewMode('cards')}
                      className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                      title="Vista de tarjetas"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleViewMode('list')}
                      className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                      title="Vista de lista"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                      title="Actualizar"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  {canEdit && (
                    <Button onClick={() => setShowCrearAlumno(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <UserPlus className="w-4 h-4 mr-2" /> Nuevo Alumno
                    </Button>
                  )}
                </div>
              </div>

              {/* Filtros */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o RUT..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
                  <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="todas" className="text-white">Todas las categorías</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroEstadoAlumno || 'activo'} onValueChange={setFiltroEstadoAlumno}>
                  <SelectTrigger className="w-full sm:w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="activo" className="text-white">Activos ({alumnosActivos})</SelectItem>
                    <SelectItem value="suspendido" className="text-white">Suspendidos ({alumnosSuspendidos})</SelectItem>
                    <SelectItem value="todos" className="text-white">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Lista de Alumnos */}
              {alumnosFiltrados.length === 0 ? (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No hay alumnos</p>
                </Card>
              ) : viewMode === 'list' ? (
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Alumno</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">RUT</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Apoderado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Pago</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Estado</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {alumnosFiltrados.map(alumno => (
                        <tr key={alumno.id} className={`hover:bg-slate-700/30 ${alumno.estado === 'inactivo' ? 'opacity-60' : ''}`}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-white">{alumno.nombre} {alumno.apellido}</p>
                              <p className="text-xs text-slate-400">{alumno.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{alumno.documento_numero}</td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {alumno.es_su_propio_apoderado ? (
                              <span className="text-slate-500 text-sm">Es su propio apoderado</span>
                            ) : alumno.apoderado ? (
                              <div className="text-sm">
                                <p className="text-slate-300">{alumno.apoderado.nombre}</p>
                                <p className="text-slate-500">{alumno.apoderado.telefono}</p>
                              </div>
                            ) : (
                              <span className="text-slate-500 text-sm">Sin apoderado</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {alumno.estado === 'activo' ? (
                              <Badge className={alumno.estado_pago === 'moroso' ? 'bg-red-600' : alumno.estado_pago === 'pendiente' ? 'bg-amber-600' : 'bg-emerald-600'}>
                                {alumno.estado_pago === 'moroso' ? 'Moroso' : alumno.estado_pago === 'pendiente' ? 'Pendiente' : 'Al día'}
                              </Badge>
                            ) : (
                              <span className="text-slate-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {alumno.estado === 'suspendido' ? (
                              <div>
                                <Badge className="bg-amber-600">Suspendido</Badge>
                                {alumno.motivo_suspension && (
                                  <p className="text-xs text-amber-400 mt-1">
                                    {alumno.motivo_suspension === 'retiro_voluntario' && 'Retiro voluntario'}
                                    {alumno.motivo_suspension === 'deuda' && 'Por deuda'}
                                    {alumno.motivo_suspension === 'inasistencia' && 'Por inasistencia'}
                                    {alumno.motivo_suspension === 'otro' && 'Otro motivo'}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-emerald-600/30 text-emerald-400 border border-emerald-600/50">
                                Activo
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" type="button" className="text-slate-400 hover:text-green-400" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVerCartolaAlumno(alumno); }} title="Ver Cartola">
                                <Receipt className="w-4 h-4" />
                              </Button>
                              {canEdit && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => handleEditarAlumno(alumno)} title="Editar">
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {alumno.estado === 'suspendido' ? (
                                    <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300" onClick={() => handleReactivarAlumno(alumno)} title="Reactivar">
                                      <UserCheck className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300" onClick={() => { setAlumnoASuspender(alumno); setShowSuspenderAlumno(true); }} title="Suspender">
                                      <UserX className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => handleEliminarAlumno(alumno.id)} title="Eliminar">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              ) : (
                /* Vista de tarjetas para Alumnos */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {alumnosFiltrados.map(alumno => (
                    <Card key={alumno.id} className={`p-4 bg-slate-800 border-slate-700 ${alumno.estado === 'suspendido' ? 'opacity-70' : ''}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold shrink-0">
                          {alumno.nombre?.charAt(0)}{alumno.apellido?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{alumno.nombre} {alumno.apellido}</h3>
                          <p className="text-sm text-slate-400 truncate">{alumno.email || alumno.telefono || 'Sin contacto'}</p>
                          <p className="text-xs text-slate-500">{alumno.documento_numero}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-2">
                        {alumno.estado === 'suspendido' ? (
                          <Badge className="bg-amber-600">Suspendido</Badge>
                        ) : (
                          <>
                            <Badge className={alumno.estado_pago === 'moroso' ? 'bg-red-600' : alumno.estado_pago === 'pendiente' ? 'bg-amber-600' : 'bg-emerald-600'}>
                              {alumno.estado_pago === 'moroso' ? 'Moroso' : alumno.estado_pago === 'pendiente' ? 'Pendiente' : 'Al día'}
                            </Badge>
                            <Badge className="bg-emerald-600/30 text-emerald-400 border border-emerald-600/50">Activo</Badge>
                          </>
                        )}
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          type="button"
                          className="text-slate-400 hover:text-green-400 hover:bg-green-600/10"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVerCartolaAlumno(alumno); }}
                          title="Ver Cartola"
                        >
                          <Receipt className="w-3 h-3" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="flex-1 text-slate-400 hover:text-white hover:bg-slate-600"
                              onClick={() => handleEditarAlumno(alumno)}
                            >
                              <Pencil className="w-3 h-3 mr-1" /> Editar
                            </Button>
                            {alumno.estado === 'suspendido' ? (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-600/10"
                                onClick={() => handleReactivarAlumno(alumno)}
                              >
                                <UserCheck className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-400 hover:text-amber-400 hover:bg-amber-600/10"
                                onClick={() => { setAlumnoASuspender(alumno); setShowSuspenderAlumno(true); }}
                              >
                                <UserX className="w-3 h-3" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-400 hover:text-red-400 hover:bg-red-600/10"
                              onClick={() => handleEliminarAlumno(alumno.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Categorías */}
          {activeSection === 'categorias' && (
            <div className="space-y-4">
              {categoriaSeleccionada ? (
                // Vista de detalle de categoría con sus alumnos
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" onClick={() => { setCategoriaSeleccionada(null); setSearchTermCategoria(''); }} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Volver
                    </Button>
                    <div>
                      <h2 className="text-xl font-bold text-white">{categoriaSeleccionada.nombre}</h2>
                      {categoriaSeleccionada.descripcion && (
                        <p className="text-sm text-slate-400">{categoriaSeleccionada.descripcion}</p>
                      )}
                    </div>
                  </div>

                  {/* Info de la categoría */}
                  <Card className="p-4 bg-slate-800/50 border-slate-700 mb-6">
                    <div className="flex flex-wrap gap-6">
                      {(categoriaSeleccionada.edad_min || categoriaSeleccionada.edad_max) && (
                        <div>
                          <p className="text-xs text-slate-500">Rango de edad</p>
                          <p className="text-sm text-white">
                            {categoriaSeleccionada.edad_min && categoriaSeleccionada.edad_max 
                              ? `${categoriaSeleccionada.edad_min} - ${categoriaSeleccionada.edad_max} años` 
                              : categoriaSeleccionada.edad_min 
                                ? `Desde ${categoriaSeleccionada.edad_min} años` 
                                : `Hasta ${categoriaSeleccionada.edad_max} años`}
                          </p>
                        </div>
                      )}
                      {categoriaSeleccionada.horarios?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Horarios</p>
                          <div className="space-y-0.5">
                            {categoriaSeleccionada.horarios.map((h, i) => (
                              <p key={i} className="text-sm text-white">
                                {DIAS_SEMANA.find(d => d.value === h.dia)?.label} {h.hora_inicio} - {h.hora_fin}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="ml-auto flex gap-2">
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => handleGenerarLinkInscripcion(categoriaSeleccionada)}>
                          <Link2 className="w-4 h-4 mr-2" /> Link Inscripción
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => handleEditarCategoria(categoriaSeleccionada)}>
                          <Pencil className="w-4 h-4 mr-2" /> Editar
                        </Button>
                      </div>
                    </div>
                  </Card>

                  {/* Alumnos de la categoría */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">
                      Alumnos en "{categoriaSeleccionada.nombre}" ({getAlumnosDeCategoria(categoriaSeleccionada.id).length})
                    </h3>
                    <Button onClick={() => setShowAgregarAlumnosCategoria(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <UserPlus className="w-4 h-4 mr-2" /> Agregar Alumnos
                    </Button>
                  </div>

                  {/* Buscador */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Buscar alumno..."
                      value={searchTermCategoria}
                      onChange={(e) => setSearchTermCategoria(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>

                  {/* Lista de alumnos */}
                  <div className="space-y-2">
                    {getAlumnosDeCategoria(categoriaSeleccionada.id)
                      .filter(a => `${a.nombre} ${a.apellido}`.toLowerCase().includes(searchTermCategoria.toLowerCase()))
                      .map(alumno => (
                        <Card key={alumno.id} className="p-3 bg-slate-800 border-slate-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                                {alumno.nombre?.charAt(0)}{alumno.apellido?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-white">{alumno.nombre} {alumno.apellido}</p>
                                <p className="text-xs text-slate-400">{alumno.email || alumno.telefono || 'Sin contacto'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={alumno.estado_pago === 'al_dia' ? 'bg-emerald-600' : alumno.estado_pago === 'pendiente' ? 'bg-amber-600' : 'bg-red-600'}>
                                {alumno.estado_pago === 'al_dia' ? 'Al día' : alumno.estado_pago === 'pendiente' ? 'Pendiente' : 'Moroso'}
                              </Badge>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                                onClick={() => handleToggleAlumnoCategoria(alumno, categoriaSeleccionada.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    {getAlumnosDeCategoria(categoriaSeleccionada.id).length === 0 && (
                      <Card className="p-8 bg-slate-800/50 border-slate-700 text-center">
                        <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No hay alumnos en esta categoría</p>
                        <Button onClick={() => setShowAgregarAlumnosCategoria(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                          <UserPlus className="w-4 h-4 mr-2" /> Agregar Alumnos
                        </Button>
                      </Card>
                    )}
                  </div>
                </div>
              ) : (
                // Vista de lista de categorías
                <>
                  <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-white">Categorías</h1>
                    <div className="flex items-center gap-3">
                      {/* Toggle de vista */}
                      <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                        <button
                          onClick={() => toggleViewMode('cards')}
                          className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                          title="Vista de tarjetas"
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleViewMode('list')}
                          className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                          title="Vista de lista"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleRefresh}
                          disabled={refreshing}
                          className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                          title="Actualizar"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                      {canEdit && (
                        <Button onClick={() => setShowCrearCategoria(true)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!isPro && categorias.length >= 1}>
                          <Plus className="w-4 h-4 mr-2" /> Nueva Categoría
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Filtros de Categorías */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Buscar categoría..."
                        value={searchCategoria}
                        onChange={(e) => setSearchCategoria(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    {sucursales.length > 0 && (
                      <Select value={filtroSucursalCategoria} onValueChange={setFiltroSucursalCategoria}>
                        <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
                          <SelectValue placeholder="Sucursal" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="todas" className="text-white">Todas las sucursales</SelectItem>
                          {sucursales.map(suc => (
                            <SelectItem key={suc.id} value={suc.id} className="text-white">{suc.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {(() => {
                    const categoriasFiltradas = categorias.filter(cat => {
                      const matchSearch = !searchCategoria || cat.nombre?.toLowerCase().includes(searchCategoria.toLowerCase());
                      const matchSucursal = filtroSucursalCategoria === 'todas' || cat.sucursal_id === filtroSucursalCategoria;
                      return matchSearch && matchSucursal;
                    });
                    
                    return categoriasFiltradas.length === 0 ? (
                    <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                      <FolderTree className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No hay categorías</p>
                      {canEdit && (
                        <Button onClick={() => setShowCrearCategoria(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                          <Plus className="w-4 h-4 mr-2" /> Crear Primera Categoría
                        </Button>
                      )}
                    </Card>
                  ) : viewMode === 'cards' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoriasFiltradas.map(cat => {
                        const alumnosCat = getAlumnosDeCategoria(cat.id);
                        const sucursalCat = sucursales.find(s => s.id === cat.sucursal_id);
                        return (
                          <Card 
                            key={cat.id} 
                            className="p-4 bg-slate-800 border-slate-700 hover:border-emerald-500/50 cursor-pointer transition-colors"
                            onClick={() => { setCategoriaSeleccionada(cat); setSearchTermCategoria(''); }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold shrink-0">
                                <FolderTree className="w-6 h-6" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-white truncate">{cat.nombre}</h3>
                                {sucursalCat && <p className="text-xs text-emerald-400">{sucursalCat.nombre}</p>}
                                {(cat.edad_min || cat.edad_max) && (
                                  <p className="text-xs text-slate-400">
                                    {cat.edad_min && cat.edad_max ? `${cat.edad_min} - ${cat.edad_max} años` :
                                     cat.edad_min ? `Desde ${cat.edad_min} años` : `Hasta ${cat.edad_max} años`}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="mt-3 flex items-center justify-between">
                              <Badge className="bg-slate-700">{alumnosCat.length} alumnos</Badge>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button size="sm" variant="ghost" className="flex-1 text-slate-400 hover:text-white hover:bg-slate-600" onClick={() => handleEditarCategoria(cat)}>
                                <Pencil className="w-3 h-3 mr-1" /> Editar
                              </Button>
                              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-600/10" onClick={() => handleEliminarCategoria(cat.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    /* Vista de lista */
                    <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-700/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Categoría</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">Sucursal</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Horarios</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Alumnos</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                          {categoriasFiltradas.map(cat => {
                            const alumnosCat = getAlumnosDeCategoria(cat.id);
                            const sucursalCat = sucursales.find(s => s.id === cat.sucursal_id);
                            return (
                              <tr key={cat.id} className="hover:bg-slate-700/30 cursor-pointer" onClick={() => { setCategoriaSeleccionada(cat); setSearchTermCategoria(''); }}>
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="font-medium text-white">{cat.nombre}</p>
                                    {(cat.edad_min || cat.edad_max) && (
                                      <p className="text-xs text-slate-400">
                                        {cat.edad_min && cat.edad_max ? `${cat.edad_min} - ${cat.edad_max} años` :
                                         cat.edad_min ? `Desde ${cat.edad_min} años` : `Hasta ${cat.edad_max} años`}
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{sucursalCat?.nombre || '-'}</td>
                                <td className="px-4 py-3 hidden lg:table-cell">
                                  {cat.horarios?.length > 0 ? (
                                    <div className="space-y-0.5">
                                      {cat.horarios.slice(0, 2).map((h, i) => (
                                        <p key={i} className="text-xs text-slate-400">
                                          {DIAS_SEMANA.find(d => d.value === h.dia)?.label?.slice(0,3)} {h.hora_inicio}-{h.hora_fin}
                                        </p>
                                      ))}
                                      {cat.horarios.length > 2 && <p className="text-xs text-slate-500">+{cat.horarios.length - 2} más</p>}
                                    </div>
                                  ) : <span className="text-slate-500 text-xs">-</span>}
                                </td>
                                <td className="px-4 py-3">
                                  <Badge className="bg-slate-700">{alumnosCat.length}</Badge>
                                </td>
                                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => handleEditarCategoria(cat)}>
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => handleEliminarCategoria(cat.id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card>
                  );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Profesores (Entrenadores) */}
          {activeSection === 'entrenadores' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Entrenadores</h1>
                <div className="flex items-center gap-3">
                  {/* Toggle de vista */}
                  <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                    <button
                      onClick={() => toggleViewMode('cards')}
                      className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                      title="Vista de tarjetas"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleViewMode('list')}
                      className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                      title="Vista de lista"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-600 disabled:opacity-50"
                      title="Actualizar"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  {canEdit && (
                    <Button onClick={() => setShowCrearEntrenador(true)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!isPro && entrenadores.length >= 1}>
                      <UserPlus className="w-4 h-4 mr-2" /> Nuevo Entrenador
                    </Button>
                  )}
                </div>
              </div>

              {/* Filtros de Entrenadores */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar entrenador..."
                    value={searchEntrenador}
                    onChange={(e) => setSearchEntrenador(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <Select value={filtroEstadoEntrenador} onValueChange={setFiltroEstadoEntrenador}>
                  <SelectTrigger className="w-full sm:w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="todos" className="text-white">Todos</SelectItem>
                    <SelectItem value="activo" className="text-white">Activos</SelectItem>
                    <SelectItem value="pendiente" className="text-white">Pendientes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(() => {
                const entrenadoresFiltrados = entrenadores.filter(ent => {
                  const matchSearch = !searchEntrenador || 
                    `${ent.nombre} ${ent.apellido}`.toLowerCase().includes(searchEntrenador.toLowerCase()) ||
                    ent.email?.toLowerCase().includes(searchEntrenador.toLowerCase());
                  const matchEstado = filtroEstadoEntrenador === 'todos' || 
                    (filtroEstadoEntrenador === 'activo' && ent.invitacion_aceptada) ||
                    (filtroEstadoEntrenador === 'pendiente' && !ent.invitacion_aceptada);
                  return matchSearch && matchEstado;
                });

                return entrenadoresFiltrados.length === 0 ? (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <UserCog className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No hay profesores</p>
                </Card>
              ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {entrenadoresFiltrados.map(ent => (
                    <Card key={ent.id} className="p-4 bg-slate-800 border-slate-700">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                          {ent.nombre?.charAt(0)}{ent.apellido?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{ent.nombre} {ent.apellido}</h3>
                          <p className="text-sm text-slate-400 truncate">{ent.email}</p>
                          {ent.especialidad && <p className="text-xs text-purple-400 mt-1">{ent.especialidad}</p>}
                          {ent.telefono && <p className="text-xs text-slate-500">{ent.telefono}</p>}
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center justify-between">
                        <Badge className={ent.invitacion_aceptada ? 'bg-emerald-600' : 'bg-amber-600'}>
                          {ent.invitacion_aceptada ? 'Activo' : 'Pendiente'}
                        </Badge>
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="mt-3 pt-3 border-t border-slate-700 flex gap-2">
                        {!ent.invitacion_aceptada && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="flex-1 text-slate-400 hover:text-amber-400 hover:bg-amber-600/10"
                            onClick={() => handleReenviarInvitacion(ent)}
                          >
                            <Mail className="w-3 h-3 mr-1" /> Reenviar
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="flex-1 text-slate-400 hover:text-white hover:bg-slate-600"
                          onClick={() => handleEditarEntrenador(ent)}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-slate-400 hover:text-red-400 hover:bg-red-600/10"
                          onClick={() => handleEliminarEntrenador(ent.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                /* Vista de lista */
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Profesor</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden md:table-cell">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300 hidden lg:table-cell">Especialidad</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Estado</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {entrenadoresFiltrados.map(ent => (
                        <tr key={ent.id} className="hover:bg-slate-700/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                                {ent.nombre?.charAt(0)}{ent.apellido?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-white">{ent.nombre} {ent.apellido}</p>
                                {ent.telefono && <p className="text-xs text-slate-500">{ent.telefono}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{ent.email}</td>
                          <td className="px-4 py-3 text-purple-400 hidden lg:table-cell">{ent.especialidad || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge className={ent.invitacion_aceptada ? 'bg-emerald-600' : 'bg-amber-600'}>
                              {ent.invitacion_aceptada ? 'Activo' : 'Pendiente'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {!ent.invitacion_aceptada && (
                                <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300" onClick={() => handleReenviarInvitacion(ent)}>
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => handleEditarEntrenador(ent)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => handleEliminarEntrenador(ent.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              );
              })()}
            </div>
          )}

          {/* Clases Abiertas / En Curso */}
          {activeSection === 'clases-hoy' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Clases en Curso</h1>
                <Button variant="outline" onClick={loadClasesAbiertas} className="border-slate-600 text-slate-300">
                  <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
                </Button>
              </div>

              {/* Filtros */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-48">
                    <label className="text-xs text-slate-400 mb-1 block">Sucursal</label>
                    <Select value={filtroClasesSucursal} onValueChange={(v) => { setFiltroClasesSucursal(v); setFiltroClasesCategoria('__all__'); }}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todas las sucursales" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todas las sucursales</SelectItem>
                        {sucursales.map(suc => (
                          <SelectItem key={suc.id} value={suc.id} className="text-white">{suc.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-48">
                    <label className="text-xs text-slate-400 mb-1 block">Categoría</label>
                    <Select value={filtroClasesCategoria} onValueChange={setFiltroClasesCategoria}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todas las categorías" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todas las categorías</SelectItem>
                        {categoriasFiltradas.map(cat => (
                          <SelectItem key={cat.id} value={cat.id} className="text-white">
                            {cat.nombre}{getSucursalNombre(cat.sucursal_id) && ` - ${getSucursalNombre(cat.sucursal_id)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-48">
                    <label className="text-xs text-slate-400 mb-1 block">Entrenador</label>
                    <Select value={filtroClasesEntrenador} onValueChange={setFiltroClasesEntrenador}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todos los entrenadores" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todos los entrenadores</SelectItem>
                        {entrenadores.map(ent => (
                          <SelectItem key={ent.id} value={ent.id} className="text-white">{ent.nombre} {ent.apellido}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(filtroClasesSucursal && filtroClasesSucursal !== '__all__') || (filtroClasesCategoria && filtroClasesCategoria !== '__all__') || (filtroClasesEntrenador && filtroClasesEntrenador !== '__all__') ? (
                    <div className="flex items-end">
                      <Button variant="ghost" size="sm" onClick={() => { setFiltroClasesSucursal('__all__'); setFiltroClasesCategoria('__all__'); setFiltroClasesEntrenador('__all__'); }} className="text-slate-400">
                        Limpiar filtros
                      </Button>
                    </div>
                  ) : null}
                </div>
              </Card>

              {clasesAbiertas.length === 0 ? (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <Play className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No hay clases en curso</p>
                </Card>
              ) : (
                <Card className="bg-slate-800 border-emerald-700/50 overflow-hidden">
                  <div className="divide-y divide-slate-700">
                    {clasesAbiertas.map(clase => {
                      const categoria = categorias.find(c => c.id === clase.categoria_id);
                      const sucursalNombre = categoria ? getSucursalNombre(categoria.sucursal_id) : '';
                      return (
                      <div key={clase.id} className="p-4 hover:bg-emerald-900/20 cursor-pointer" onClick={() => handleAbrirClase(clase.id)}>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-white">
                              {clase.categoria_nombre}
                              {sucursalNombre && <span className="text-emerald-400 ml-2">• {sucursalNombre}</span>}
                            </p>
                            <p className="text-sm text-slate-400">
                              {clase.fecha} • Iniciada: {clase.hora_inicio}
                              {clase.entrenador_principal_nombre && ` • ${clase.entrenador_principal_nombre}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-emerald-600">En Curso</Badge>
                            <ChevronRight className="w-5 h-5 text-emerald-400" />
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Historial de Clases */}
          {activeSection === 'clases-historial' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-white">Historial de Clases</h1>
              
              {/* Filtros */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-40">
                    <label className="text-xs text-slate-400 mb-1 block">Sucursal</label>
                    <Select value={filtroClasesSucursal} onValueChange={(v) => { setFiltroClasesSucursal(v); setFiltroClasesCategoria('__all__'); }}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todas las sucursales</SelectItem>
                        {sucursales.map(suc => (
                          <SelectItem key={suc.id} value={suc.id} className="text-white">{suc.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-40">
                    <label className="text-xs text-slate-400 mb-1 block">Categoría</label>
                    <Select value={filtroClasesCategoria} onValueChange={setFiltroClasesCategoria}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todas las categorías</SelectItem>
                        {categoriasFiltradas.map(cat => (
                          <SelectItem key={cat.id} value={cat.id} className="text-white">
                            {cat.nombre}{getSucursalNombre(cat.sucursal_id) && ` - ${getSucursalNombre(cat.sucursal_id)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-40">
                    <label className="text-xs text-slate-400 mb-1 block">Entrenador</label>
                    <Select value={filtroClasesEntrenador} onValueChange={setFiltroClasesEntrenador}>
                      <SelectTrigger className="bg-slate-700 border-slate-600">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="__all__" className="text-white">Todos los entrenadores</SelectItem>
                        {entrenadores.map(ent => (
                          <SelectItem key={ent.id} value={ent.id} className="text-white">{ent.nombre} {ent.apellido}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <label className="text-xs text-slate-400 mb-1 block">Fecha</label>
                    <Input
                      type="date"
                      value={filtroClasesFecha}
                      onChange={(e) => setFiltroClasesFecha(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  {((filtroClasesSucursal && filtroClasesSucursal !== '__all__') || (filtroClasesCategoria && filtroClasesCategoria !== '__all__') || (filtroClasesEntrenador && filtroClasesEntrenador !== '__all__') || filtroClasesFecha) && (
                    <div className="flex items-end">
                      <Button variant="ghost" size="sm" onClick={() => { setFiltroClasesSucursal('__all__'); setFiltroClasesCategoria('__all__'); setFiltroClasesEntrenador('__all__'); setFiltroClasesFecha(''); }} className="text-slate-400">
                        Limpiar
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
              
              {clasesHistorial.length === 0 ? (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No hay clases registradas</p>
                </Card>
              ) : (
                <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                  <div className="divide-y divide-slate-700">
                    {clasesHistorial.map(clase => {
                      const categoria = categorias.find(c => c.id === clase.categoria_id);
                      const sucursalNombre = categoria ? getSucursalNombre(categoria.sucursal_id) : '';
                      return (
                      <div key={clase.id} className="p-4 hover:bg-slate-700/30 cursor-pointer" onClick={() => handleAbrirClase(clase.id)}>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-white">
                              {clase.categoria_nombre}
                              {sucursalNombre && <span className="text-emerald-400 ml-2">• {sucursalNombre}</span>}
                            </p>
                            <p className="text-sm text-slate-400">
                              {clase.fecha} • {clase.hora_inicio} - {clase.hora_fin || '--:--'}
                              {clase.entrenador_principal_nombre && ` • ${clase.entrenador_principal_nombre}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                              {clase.total_presentes || 0} presentes
                            </Badge>
                            <Badge className={clase.estado === 'abierta' ? 'bg-emerald-600' : 'bg-slate-600'}>
                              {clase.estado === 'abierta' ? 'En Curso' : 'Cerrada'}
                            </Badge>
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}
          {/* Solicitudes Pendientes */}
          {activeSection === 'solicitudes' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-white">Solicitudes de Inscripción</h1>
              
              {solicitudesPendientes.length === 0 ? (
                <Card className="p-8 bg-slate-800 border-slate-700 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <p className="text-slate-400">No hay solicitudes pendientes</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {solicitudesPendientes.map(alumno => (
                    <Card key={alumno.id} className="p-4 bg-slate-800 border-slate-700">
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-white">{alumno.nombre} {alumno.apellido}</h3>
                          <p className="text-sm text-slate-400">{alumno.documento_numero}</p>
                          {alumno.email && <p className="text-sm text-slate-500">{alumno.email}</p>}
                          {alumno.apoderado && (
                            <p className="text-xs text-slate-500 mt-1">
                              Apoderado: {alumno.apoderado.nombre} - {alumno.apoderado.telefono}
                            </p>
                          )}
                          {alumno.categoria_ids?.length > 0 && (
                            <p className="text-xs text-emerald-400 mt-1">
                              Categoría: {categorias.find(c => c.id === alumno.categoria_ids[0])?.nombre}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleRechazarInscripcion(alumno.id)} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/20">
                            <XCircle className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                          <Button onClick={() => handleAprobarInscripcion(alumno.id)} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Configuración */}
          {activeSection === 'configuracion' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-white">Configuración</h1>
              
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-4">Datos de la Academia</h2>
                
                {/* Logo */}
                <div className="flex items-center gap-4 mb-6">
                  {academiaActual.logo ? (
                    <img src={academiaActual.logo} alt="Logo" className="w-24 h-24 rounded-xl object-cover" />
                  ) : (
                    <div className="w-24 h-24 bg-emerald-600/20 rounded-xl flex items-center justify-center">
                      <DisciplinaIcon className="w-12 h-12 text-emerald-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-white font-semibold text-lg">{academiaActual.nombre}</p>
                    <p className="text-emerald-400 text-sm">{DISCIPLINAS.find(d => d.id === academiaActual.disciplina)?.nombre}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <p className="text-white">{academiaActual.email_contacto || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Teléfono</Label>
                    <p className="text-white">{academiaActual.telefono_contacto || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Dirección</Label>
                    <p className="text-white">{academiaActual.direccion || '-'}</p>
                  </div>
                </div>
                <Button onClick={() => {
                  setAcademiaForm({
                    nombre: academiaActual.nombre || '',
                    disciplina: academiaActual.disciplina || 'futbol',
                    descripcion: academiaActual.descripcion || '',
                    email_contacto: academiaActual.email_contacto || '',
                    telefono_contacto: academiaActual.telefono_contacto || '',
                    direccion: academiaActual.direccion || '',
                    pais: academiaActual.pais || 'chile',
                    logo: academiaActual.logo || ''
                  });
                  setShowEditarAcademia(true);
                }} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  <Pencil className="w-4 h-4 mr-2" /> Editar
                </Button>
              </Card>

              {/* Portal de Entrenadores */}
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-2">Acceso para Entrenadores</h2>
                <p className="text-slate-400 text-sm mb-4">
                  Los entrenadores pueden acceder con su email y contraseña en este link.
                </p>
                <div className="bg-slate-900 p-3 rounded-lg flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/academia/${academiaActual.id}/login`}
                    className="flex-1 bg-transparent text-purple-400 text-sm outline-none"
                  />
                  <Button size="sm" onClick={() => {
                    copyToClipboard(`${window.location.origin}/academia/${academiaActual.id}/login`);
                    toast.success('Link copiado');
                  }} className="bg-purple-600 hover:bg-purple-700">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </Card>

              {/* Portal de Consulta para Alumnos */}
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-2">Portal de Consulta para Alumnos</h2>
                <p className="text-slate-400 text-sm mb-4">
                  Los alumnos pueden consultar su estado de pago y asistencia en este link.
                </p>
                <div className="bg-slate-900 p-3 rounded-lg flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/academia/${academiaActual.id}/consulta`}
                    className="flex-1 bg-transparent text-blue-400 text-sm outline-none"
                  />
                  <Button size="sm" onClick={() => {
                    copyToClipboard(`${window.location.origin}/academia/${academiaActual.id}/consulta`);
                    toast.success('Link copiado');
                  }} className="bg-blue-600 hover:bg-blue-700">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </Card>

              {/* Datos Bancarios */}
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-2">Datos Bancarios</h2>
                <p className="text-slate-400 text-sm mb-4">
                  Información para recibir pagos de los alumnos.
                </p>
                
                {academiaActual.datos_bancarios?.banco ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400 text-xs">Banco</Label>
                        <p className="text-white">{academiaActual.datos_bancarios.banco}</p>
                      </div>
                      <div>
                        <Label className="text-slate-400 text-xs">Tipo de Cuenta</Label>
                        <p className="text-white">{academiaActual.datos_bancarios.tipo_cuenta === 'corriente' ? 'Cuenta Corriente' : academiaActual.datos_bancarios.tipo_cuenta === 'vista' ? 'Cuenta Vista' : 'Cuenta de Ahorro'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-400 text-xs">N° de Cuenta</Label>
                        <p className="text-white font-mono">{academiaActual.datos_bancarios.numero_cuenta}</p>
                      </div>
                      <div>
                        <Label className="text-slate-400 text-xs">RUT Titular</Label>
                        <p className="text-white">{academiaActual.datos_bancarios.rut_titular}</p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-400 text-xs">Nombre Titular</Label>
                        <p className="text-white">{academiaActual.datos_bancarios.nombre_titular}</p>
                      </div>
                      {academiaActual.datos_bancarios.email_notificacion && (
                        <div className="col-span-2">
                          <Label className="text-slate-400 text-xs">Email para Notificaciones</Label>
                          <p className="text-white">{academiaActual.datos_bancarios.email_notificacion}</p>
                        </div>
                      )}
                    </div>
                    <Button onClick={() => setShowEditarDatosBancarios(true)} variant="outline" className="border-slate-600 text-slate-300">
                      <Pencil className="w-4 h-4 mr-2" /> Editar Datos Bancarios
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Wallet className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm mb-3">No hay datos bancarios configurados</p>
                    <Button onClick={() => setShowEditarDatosBancarios(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" /> Agregar Datos Bancarios
                    </Button>
                  </div>
                )}

                {/* Código de Pago para Transferencias */}
                {academiaActual?.codigo_pago && (
                  <div className="mt-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4">
                    <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Código de Pago
                    </h4>
                    <div className="flex items-center gap-3 mb-2">
                      <code className="bg-slate-800 px-3 py-2 rounded text-lg font-mono text-white">
                        {academiaActual.codigo_pago}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-emerald-600"
                        onClick={() => {
                          navigator.clipboard.writeText(academiaActual.codigo_pago);
                          toast.success('Código copiado');
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copiar
                      </Button>
                    </div>
                    <p className="text-sm text-slate-400">
                      Indica a tus alumnos que al transferir, pongan en el motivo:
                    </p>
                    <p className="text-sm text-white mt-1 font-medium">
                      {academiaActual.codigo_pago} + RUT del alumno
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Ejemplo: <span className="text-slate-300">{academiaActual.codigo_pago} 12345678-9</span>
                    </p>
                  </div>
                )}
              </Card>

              {/* Plan */}
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h2 className="text-lg font-semibold text-white mb-2">Plan Actual</h2>
                <Badge className={`${isPro ? 'bg-emerald-600' : 'bg-slate-600'} text-lg px-4 py-1`}>
                  {isPro ? 'Pro' : 'Free'}
                </Badge>
                {!isPro && (
                  <div className="mt-4">
                    <p className="text-slate-400 text-sm mb-2">Límites del plan Free:</p>
                    <ul className="text-sm text-slate-500 space-y-1">
                      <li>• 1 categoría</li>
                      <li>• 20 alumnos</li>
                      <li>• 1 entrenador</li>
                    </ul>
                    <Button className="mt-4 bg-gradient-to-r from-emerald-600 to-teal-600" onClick={handleOpenUpgrade}>
                      Actualizar a Pro - $20 USD/mes
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Finanzas - Solo Pro */}
          {activeSection === 'finanzas' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Resumen Financiero</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="resumen" 
              />
            </div>
          )}

          {/* Planes de Cuotas */}
          {activeSection === 'planes-cuotas' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Planes de Cuotas</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="planes"
                viewMode={viewMode}
                onToggleViewMode={toggleViewMode}
                onRefresh={handleRefresh}
                refreshing={refreshing}
              />
            </div>
          )}

          {/* Asignar Plan Individual */}
          {activeSection === 'planes-asignar-individual' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Asignar Plan - Individual</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="asignar-individual" 
              />
            </div>
          )}

          {/* Asignar Plan Masivo */}
          {activeSection === 'planes-asignar-masivo' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Asignar Plan - Masivo</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="asignar-masivo" 
              />
            </div>
          )}

          {/* Pagos - Registrar Ingreso */}
          {activeSection === 'pagos-ingresos' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Registrar Ingreso</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="ingresos" 
              />
            </div>
          )}

          {/* Pagos - Historial */}
          {activeSection === 'pagos-historial' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Historial de Pagos</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="historial" 
              />
            </div>
          )}

          {/* Pagos - Crear Cargo */}
          {activeSection === 'pagos-cargo' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Crear Cargo Excepcional</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="cargo" 
              />
            </div>
          )}

          {/* Pagos - Crear Excepción */}
          {activeSection === 'pagos-excepcion' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Crear Excepción de Pago</h1>
              <FinanzasModuleAcademia 
                academiaId={academiaActual.id} 
                canEdit={true} 
                categorias={categorias} 
                alumnos={alumnos} 
                section="excepcion" 
              />
            </div>
          )}

          {/* Contabilidad - Ingresos */}
          {activeSection === 'contabilidad-ingresos' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Ingresos de la Academia</h1>
              <ContabilidadModuleAcademia academiaId={academiaActual.id} section="ingresos" />
            </div>
          )}

          {/* Contabilidad - Egresos */}
          {activeSection === 'contabilidad-egresos' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Egresos de la Academia</h1>
              <ContabilidadModuleAcademia academiaId={academiaActual.id} section="egresos" />
            </div>
          )}

          {/* Contabilidad - Cuentas por Pagar */}
          {activeSection === 'contabilidad-cuentas' && isPro && (
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-6">Cuentas por Pagar</h1>
              <ContabilidadModuleAcademia academiaId={academiaActual.id} section="cuentas" />
            </div>
          )}

          {/* Sucursales */}
          {activeSection === 'sucursales' && (
            <SucursalesModule 
              academiaId={academiaActual.id}
              categorias={categorias}
              viewMode={viewMode}
              onToggleViewMode={toggleViewMode}
              onSucursalesChange={(newSucursales) => setSucursales(newSucursales)}
              onRefresh={handleRefresh}
              refreshing={refreshing}
              onSelectSucursal={(suc) => {
                // Por ahora solo muestra info, en el futuro puede filtrar
                toast.info(`Sucursal: ${suc.nombre}`);
              }}
            />
          )}

          {/* Usuarios de Academia */}
          {activeSection === 'usuarios' && (
            <UsuariosAcademiaModule 
              academiaId={academiaActual.id} 
              adminUserId={academiaActual.admin_user_id}
              currentUserId={user?.id}
            />
          )}

          {/* Gestión Dashboard */}
          {activeSection === 'gestion' && (
            <GestionDashboard 
              academiaId={academiaActual.id} 
              sucursales={sucursales} 
              categorias={categorias}
            />
          )}
        </main>
      </div>

      {/* ==================== MODALES ==================== */}

      {/* Modal Crear Alumno */}
      <Dialog open={showCrearAlumno} onOpenChange={setShowCrearAlumno}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Nuevo Alumno</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={alumnoForm.nombre}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, nombre: e.target.value })}
                  placeholder="Juan"
                  className="bg-slate-700 border-slate-600 placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label>Apellido *</Label>
                <Input
                  value={alumnoForm.apellido}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, apellido: e.target.value })}
                  placeholder="Pérez"
                  className="bg-slate-700 border-slate-600 placeholder:text-slate-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>RUT *</Label>
                <Input
                  value={alumnoForm.documento_numero}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, documento_numero: formatRut(e.target.value) })}
                  placeholder="12.345.678-9"
                  className="bg-slate-700 border-slate-600 placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label>Fecha de Nacimiento</Label>
                <Input
                  type="date"
                  value={alumnoForm.fecha_nacimiento}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, fecha_nacimiento: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={alumnoForm.email}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, email: e.target.value })}
                  placeholder="alumno@email.com"
                  className="bg-slate-700 border-slate-600 placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={alumnoForm.telefono}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, telefono: e.target.value })}
                  placeholder="+56 9 1234 5678"
                  className="bg-slate-700 border-slate-600 placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="es_apoderado"
                checked={alumnoForm.es_su_propio_apoderado}
                onCheckedChange={(checked) => setAlumnoForm({ ...alumnoForm, es_su_propio_apoderado: checked })}
              />
              <label htmlFor="es_apoderado" className="text-sm text-slate-300">Es mayor de edad (su propio apoderado)</label>
            </div>

            {!alumnoForm.es_su_propio_apoderado && (
              <div className="bg-slate-900/50 p-4 rounded-lg border border-emerald-600/30 space-y-3">
                <h4 className="font-medium text-emerald-400 border-b border-slate-700 pb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Datos del Apoderado
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Nombre del Apoderado</Label>
                    <Input
                      value={alumnoForm.apoderado.nombre}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, nombre: e.target.value } })}
                      placeholder="María González"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Teléfono del Apoderado</Label>
                    <Input
                      value={alumnoForm.apoderado.telefono}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, telefono: e.target.value } })}
                      placeholder="+56 9 8765 4321"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Email del Apoderado</Label>
                  <Input
                    type="email"
                    value={alumnoForm.apoderado.email}
                    onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, email: e.target.value } })}
                    placeholder="apoderado@email.com"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Parentesco</Label>
                  <Select 
                    value={alumnoForm.apoderado.parentesco || 'padre'} 
                    onValueChange={(v) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, parentesco: v } })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="padre" className="text-white">Padre</SelectItem>
                      <SelectItem value="madre" className="text-white">Madre</SelectItem>
                      <SelectItem value="tutor" className="text-white">Tutor Legal</SelectItem>
                      <SelectItem value="abuelo" className="text-white">Abuelo/a</SelectItem>
                      <SelectItem value="otro" className="text-white">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowCrearAlumno(false)} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleCrearAlumno} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Crear Alumno</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Categoría */}
      <Dialog open={showCrearCategoria} onOpenChange={setShowCrearCategoria}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Nueva Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sucursal *</Label>
              <Select value={categoriaForm.sucursal_id} onValueChange={(v) => setCategoriaForm({ ...categoriaForm, sucursal_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Selecciona una sucursal" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {sucursales.map(suc => (
                    <SelectItem key={suc.id} value={suc.id} className="text-white">{suc.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sucursales.length === 0 && (
                <p className="text-xs text-amber-400 mt-1">Primero debes crear al menos una sucursal</p>
              )}
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={categoriaForm.nombre}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, nombre: e.target.value })}
                placeholder="Ej: Sub-12, Adultos Principiantes"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Edad Mínima</Label>
                <Input
                  type="number"
                  value={categoriaForm.edad_min}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_min: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Edad Máxima</Label>
                <Input
                  type="number"
                  value={categoriaForm.edad_max}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_max: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>

            {/* Horarios */}
            <div>
              <Label>Horarios</Label>
              <div className="space-y-2 mt-2">
                {categoriaForm.horarios.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-700/50 p-2 rounded">
                    <span className="text-sm text-slate-300">{DIAS_SEMANA.find(d => d.value === h.dia)?.label}</span>
                    <span className="text-sm text-slate-400">{h.hora_inicio} - {h.hora_fin}</span>
                    <Button size="sm" variant="ghost" onClick={() => eliminarHorario(i)} className="ml-auto text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-2">
                <Select value={String(horarioTemp.dia)} onValueChange={(v) => setHorarioTemp({ ...horarioTemp, dia: parseInt(v) })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {DIAS_SEMANA.map(d => (
                      <SelectItem key={d.value} value={String(d.value)} className="text-white">{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="time"
                  value={horarioTemp.hora_inicio}
                  onChange={(e) => setHorarioTemp({ ...horarioTemp, hora_inicio: e.target.value })}
                  className="bg-slate-700 border-slate-600 w-24"
                />
                <Input
                  type="time"
                  value={horarioTemp.hora_fin}
                  onChange={(e) => setHorarioTemp({ ...horarioTemp, hora_fin: e.target.value })}
                  className="bg-slate-700 border-slate-600 w-24"
                />
                <Button onClick={agregarHorario} size="sm" className="bg-emerald-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowCrearCategoria(false)} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleCrearCategoria} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Crear Categoría</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Categoría */}
      <Dialog open={showEditarCategoria} onOpenChange={setShowEditarCategoria}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Editar Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sucursal *</Label>
              <Select value={categoriaForm.sucursal_id} onValueChange={(v) => setCategoriaForm({ ...categoriaForm, sucursal_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Selecciona una sucursal" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {sucursales.map(suc => (
                    <SelectItem key={suc.id} value={suc.id} className="text-white">{suc.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={categoriaForm.nombre}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, nombre: e.target.value })}
                placeholder="Ej: Sub-12, Adultos Principiantes"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Edad Mínima</Label>
                <Input
                  type="number"
                  value={categoriaForm.edad_min}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_min: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Edad Máxima</Label>
                <Input
                  type="number"
                  value={categoriaForm.edad_max}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, edad_max: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>

            {/* Horarios */}
            <div>
              <Label>Horarios</Label>
              <div className="space-y-2 mt-2">
                {categoriaForm.horarios.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-700/50 p-2 rounded">
                    <span className="text-sm text-slate-300">{DIAS_SEMANA.find(d => d.value === h.dia)?.label}</span>
                    <span className="text-sm text-slate-400">{h.hora_inicio} - {h.hora_fin}</span>
                    <Button size="sm" variant="ghost" onClick={() => eliminarHorario(i)} className="ml-auto text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-2">
                <Select value={String(horarioTemp.dia)} onValueChange={(v) => setHorarioTemp({ ...horarioTemp, dia: parseInt(v) })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {DIAS_SEMANA.map(d => (
                      <SelectItem key={d.value} value={String(d.value)} className="text-white">{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="time"
                  value={horarioTemp.hora_inicio}
                  onChange={(e) => setHorarioTemp({ ...horarioTemp, hora_inicio: e.target.value })}
                  className="bg-slate-700 border-slate-600 w-24"
                />
                <Input
                  type="time"
                  value={horarioTemp.hora_fin}
                  onChange={(e) => setHorarioTemp({ ...horarioTemp, hora_fin: e.target.value })}
                  className="bg-slate-700 border-slate-600 w-24"
                />
                <Button onClick={agregarHorario} size="sm" className="bg-emerald-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => { setShowEditarCategoria(false); setCategoriaEditando(null); }} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleGuardarCategoria} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Guardar Cambios</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Agregar Alumnos a Categoría */}
      <Dialog open={showAgregarAlumnosCategoria} onOpenChange={setShowAgregarAlumnosCategoria}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar Alumnos a "{categoriaSeleccionada?.nombre}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {getAlumnosSinCategoria(categoriaSeleccionada?.id).length === 0 ? (
              <p className="text-slate-400 text-center py-4">Todos los alumnos ya están en esta categoría</p>
            ) : (
              <div className="space-y-2">
                {getAlumnosSinCategoria(categoriaSeleccionada?.id).map(alumno => (
                  <Card 
                    key={alumno.id} 
                    className="p-3 bg-slate-700 border-slate-600 hover:border-emerald-500 cursor-pointer transition-colors"
                    onClick={() => {
                      handleToggleAlumnoCategoria(alumno, categoriaSeleccionada?.id);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                          {alumno.nombre?.charAt(0)}{alumno.apellido?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{alumno.nombre} {alumno.apellido}</p>
                          <p className="text-xs text-slate-400">{alumno.email || 'Sin email'}</p>
                        </div>
                      </div>
                      <Plus className="w-5 h-5 text-emerald-400" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
            <Button onClick={() => setShowAgregarAlumnosCategoria(false)} variant="outline" className="w-full">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Alumno */}
      <Dialog open={showEditarAlumno} onOpenChange={setShowEditarAlumno}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Alumno</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={alumnoForm.nombre}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, nombre: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Apellido *</Label>
                <Input
                  value={alumnoForm.apellido}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, apellido: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Documento/RUT</Label>
                <Input
                  value={alumnoForm.documento_numero}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, documento_numero: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Fecha Nacimiento</Label>
                <Input
                  type="date"
                  value={alumnoForm.fecha_nacimiento}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, fecha_nacimiento: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={alumnoForm.email}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, email: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={alumnoForm.telefono}
                  onChange={(e) => setAlumnoForm({ ...alumnoForm, telefono: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>

            {/* Categorías */}
            <div>
              <Label>Categorías</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {categorias.map(cat => (
                  <div 
                    key={cat.id} 
                    className="flex items-center gap-3 p-2 rounded bg-slate-700/50 hover:bg-slate-700 cursor-pointer"
                    onClick={() => {
                      const current = alumnoForm.categoria_ids || [];
                      const updated = current.includes(cat.id)
                        ? current.filter(id => id !== cat.id)
                        : [...current, cat.id];
                      setAlumnoForm({ ...alumnoForm, categoria_ids: updated });
                    }}
                  >
                    <Checkbox 
                      checked={alumnoForm.categoria_ids?.includes(cat.id)} 
                      className="border-slate-500"
                    />
                    <span className="text-sm text-white">{cat.nombre}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Apoderado */}
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={alumnoForm.es_su_propio_apoderado}
                onCheckedChange={(checked) => setAlumnoForm({ ...alumnoForm, es_su_propio_apoderado: checked })}
                className="border-slate-500"
              />
              <Label className="cursor-pointer" onClick={() => setAlumnoForm({ ...alumnoForm, es_su_propio_apoderado: !alumnoForm.es_su_propio_apoderado })}>
                Es su propio apoderado (mayor de edad)
              </Label>
            </div>

            {!alumnoForm.es_su_propio_apoderado && (
              <div className="p-4 bg-slate-700/70 rounded-lg space-y-3 border border-slate-600">
                <p className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Datos del Apoderado
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Nombre</Label>
                    <Input
                      placeholder="Nombre del apoderado"
                      value={alumnoForm.apoderado?.nombre || ''}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, nombre: e.target.value } })}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Apellido</Label>
                    <Input
                      placeholder="Apellido del apoderado"
                      value={alumnoForm.apoderado?.apellido || ''}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, apellido: e.target.value } })}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Teléfono</Label>
                    <Input
                      placeholder="+56 9 XXXX XXXX"
                      value={alumnoForm.apoderado?.telefono || ''}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, telefono: e.target.value } })}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Email</Label>
                    <Input
                      placeholder="email@ejemplo.com"
                      type="email"
                      value={alumnoForm.apoderado?.email || ''}
                      onChange={(e) => setAlumnoForm({ ...alumnoForm, apoderado: { ...alumnoForm.apoderado, email: e.target.value } })}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={() => { setShowEditarAlumno(false); setAlumnoEditando(null); }} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleGuardarAlumno} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Guardar Cambios</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Entrenador */}
      <Dialog open={showCrearEntrenador} onOpenChange={setShowCrearEntrenador}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Nuevo Entrenador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={entrenadorForm.nombre}
                  onChange={(e) => setEntrenadorForm({ ...entrenadorForm, nombre: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input
                  value={entrenadorForm.apellido}
                  onChange={(e) => setEntrenadorForm({ ...entrenadorForm, apellido: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={entrenadorForm.email}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Teléfono</Label>
              <Input
                value={entrenadorForm.telefono}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, telefono: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Especialidad</Label>
              <Input
                value={entrenadorForm.especialidad}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, especialidad: e.target.value })}
                placeholder="Ej: Karate infantil"
                className="bg-slate-700 border-slate-600"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowCrearEntrenador(false)} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleCrearEntrenador} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Mail className="w-4 h-4 mr-2" /> Crear y Enviar Invitación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Link de Inscripción */}
      <Dialog open={showLinkInscripcion} onOpenChange={setShowLinkInscripcion}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-emerald-400" />
              Link de Inscripción
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              Comparte este link para que nuevos alumnos se inscriban a <span className="font-semibold text-white">{categoriaParaLink?.nombre}</span>
            </p>
            
            <div className="bg-slate-900 p-3 rounded-lg flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={linkInscripcion || ''}
                className="flex-1 bg-transparent text-emerald-400 text-sm outline-none"
              />
              <Button size="sm" onClick={copiarLink} className="bg-emerald-600 hover:bg-emerald-700">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-xs text-slate-500">
              Este link permite que cualquier persona complete el formulario de inscripción. 
              Recibirás una notificación para aprobar o rechazar cada solicitud.
            </p>
            
            <Button onClick={() => setShowLinkInscripcion(false)} variant="outline" className="w-full">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Academia */}
      <Dialog open={showEditarAcademia} onOpenChange={setShowEditarAcademia}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Academia</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Logo */}
            <div>
              <Label>Logo de la Academia</Label>
              <div className="flex items-center gap-4 mt-2">
                {academiaForm.logo ? (
                  <img src={academiaForm.logo} alt="Logo" className="w-20 h-20 rounded-xl object-cover" />
                ) : (
                  <div className="w-20 h-20 bg-emerald-600/20 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-10 h-10 text-emerald-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <div className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-300 transition-colors">
                    {academiaForm.logo ? 'Cambiar logo' : 'Subir logo'}
                  </div>
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                </label>
                {academiaForm.logo && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setAcademiaForm({ ...academiaForm, logo: '' })}
                    className="text-red-400 hover:text-red-300"
                  >
                    Quitar
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <Label>Nombre</Label>
              <Input
                value={academiaForm.nombre}
                onChange={(e) => setAcademiaForm({ ...academiaForm, nombre: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Disciplina</Label>
              <Select value={academiaForm.disciplina} onValueChange={(v) => setAcademiaForm({ ...academiaForm, disciplina: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {DISCIPLINAS.map(d => (
                    <SelectItem key={d.id} value={d.id} className="text-white">{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={academiaForm.telefono_contacto}
                  onChange={(e) => setAcademiaForm({ ...academiaForm, telefono_contacto: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={academiaForm.email_contacto}
                  onChange={(e) => setAcademiaForm({ ...academiaForm, email_contacto: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={academiaForm.direccion}
                onChange={(e) => setAcademiaForm({ ...academiaForm, direccion: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowEditarAcademia(false)} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleGuardarAcademia} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Datos Bancarios */}
      <Dialog open={showEditarDatosBancarios} onOpenChange={(open) => {
        if (open && academiaActual?.datos_bancarios) {
          setDatosBancariosForm({
            banco: academiaActual.datos_bancarios.banco || '',
            tipo_cuenta: academiaActual.datos_bancarios.tipo_cuenta || 'corriente',
            numero_cuenta: academiaActual.datos_bancarios.numero_cuenta || '',
            rut_titular: academiaActual.datos_bancarios.rut_titular || '',
            nombre_titular: academiaActual.datos_bancarios.nombre_titular || '',
            email_notificacion: academiaActual.datos_bancarios.email_notificacion || ''
          });
        }
        setShowEditarDatosBancarios(open);
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Datos Bancarios</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Banco *</Label>
              <Select value={datosBancariosForm.banco} onValueChange={(v) => setDatosBancariosForm({ ...datosBancariosForm, banco: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Seleccionar banco" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="Banco de Chile" className="text-white">Banco de Chile</SelectItem>
                  <SelectItem value="Banco Estado" className="text-white">Banco Estado</SelectItem>
                  <SelectItem value="Banco Santander" className="text-white">Banco Santander</SelectItem>
                  <SelectItem value="BCI" className="text-white">BCI</SelectItem>
                  <SelectItem value="Scotiabank" className="text-white">Scotiabank</SelectItem>
                  <SelectItem value="Banco Itaú" className="text-white">Banco Itaú</SelectItem>
                  <SelectItem value="Banco BICE" className="text-white">Banco BICE</SelectItem>
                  <SelectItem value="Banco Security" className="text-white">Banco Security</SelectItem>
                  <SelectItem value="Banco Falabella" className="text-white">Banco Falabella</SelectItem>
                  <SelectItem value="Banco Ripley" className="text-white">Banco Ripley</SelectItem>
                  <SelectItem value="Banco Consorcio" className="text-white">Banco Consorcio</SelectItem>
                  <SelectItem value="TAPP" className="text-white">TAPP</SelectItem>
                  <SelectItem value="Mercado Pago" className="text-white">Mercado Pago</SelectItem>
                  <SelectItem value="Otro" className="text-white">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de Cuenta *</Label>
                <Select value={datosBancariosForm.tipo_cuenta} onValueChange={(v) => setDatosBancariosForm({ ...datosBancariosForm, tipo_cuenta: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="corriente" className="text-white">Cuenta Corriente</SelectItem>
                    <SelectItem value="vista" className="text-white">Cuenta Vista / RUT</SelectItem>
                    <SelectItem value="ahorro" className="text-white">Cuenta de Ahorro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N° de Cuenta *</Label>
                <Input
                  value={datosBancariosForm.numero_cuenta}
                  onChange={(e) => setDatosBancariosForm({ ...datosBancariosForm, numero_cuenta: e.target.value })}
                  placeholder="123456789"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>RUT Titular *</Label>
                <Input
                  value={datosBancariosForm.rut_titular}
                  onChange={(e) => setDatosBancariosForm({ ...datosBancariosForm, rut_titular: e.target.value })}
                  placeholder="12.345.678-9"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Nombre Titular *</Label>
                <Input
                  value={datosBancariosForm.nombre_titular}
                  onChange={(e) => setDatosBancariosForm({ ...datosBancariosForm, nombre_titular: e.target.value })}
                  placeholder="Nombre completo"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div>
              <Label>Email para Notificaciones (opcional)</Label>
              <Input
                type="email"
                value={datosBancariosForm.email_notificacion}
                onChange={(e) => setDatosBancariosForm({ ...datosBancariosForm, email_notificacion: e.target.value })}
                placeholder="pagos@academia.cl"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowEditarDatosBancarios(false)} variant="outline" className="flex-1">Cancelar</Button>
              <Button onClick={handleGuardarDatosBancarios} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Entrenador */}
      <Dialog open={showEditarEntrenador} onOpenChange={setShowEditarEntrenador}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Editar Entrenador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={entrenadorForm.nombre}
                  onChange={(e) => setEntrenadorForm({ ...entrenadorForm, nombre: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input
                  value={entrenadorForm.apellido}
                  onChange={(e) => setEntrenadorForm({ ...entrenadorForm, apellido: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={entrenadorForm.email}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600"
                disabled={entrenadorEditando?.invitacion_aceptada}
              />
              {entrenadorEditando?.invitacion_aceptada && (
                <p className="text-xs text-slate-500 mt-1">No se puede cambiar el email de un entrenador activo</p>
              )}
            </div>
            
            <div>
              <Label>Teléfono</Label>
              <Input
                value={entrenadorForm.telefono}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, telefono: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Especialidad</Label>
              <Input
                value={entrenadorForm.especialidad}
                onChange={(e) => setEntrenadorForm({ ...entrenadorForm, especialidad: e.target.value })}
                placeholder="Ej: Karate infantil"
                className="bg-slate-700 border-slate-600"
              />
            </div>

            {/* Sección cambiar contraseña - Solo si ya aceptó la invitación */}
            {entrenadorEditando?.invitacion_aceptada && (
              <div className="border-t border-slate-700 pt-4">
                {!showCambiarPassword ? (
                  <Button 
                    variant="outline" 
                    className="w-full border-amber-600/50 text-amber-400 hover:bg-amber-600/20"
                    onClick={() => setShowCambiarPassword(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" /> Cambiar Contraseña
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-amber-400">Nueva Contraseña</Label>
                      <Input
                        type="password"
                        value={nuevaPasswordEntrenador}
                        onChange={(e) => setNuevaPasswordEntrenador(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="bg-slate-700 border-slate-600 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Confirmar Contraseña</Label>
                      <Input
                        type="password"
                        value={confirmarPasswordEntrenador}
                        onChange={(e) => setConfirmarPasswordEntrenador(e.target.value)}
                        placeholder="Repite la contraseña"
                        className={`bg-slate-700 border-slate-600 mt-1 ${confirmarPasswordEntrenador && nuevaPasswordEntrenador !== confirmarPasswordEntrenador ? 'border-red-500' : ''}`}
                      />
                      {confirmarPasswordEntrenador && nuevaPasswordEntrenador !== confirmarPasswordEntrenador && (
                        <p className="text-xs text-red-400 mt-1">Las contraseñas no coinciden</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        className="flex-1 border-slate-600"
                        onClick={() => { setShowCambiarPassword(false); setNuevaPasswordEntrenador(''); setConfirmarPasswordEntrenador(''); }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleCambiarPasswordEntrenador}
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        disabled={!nuevaPasswordEntrenador || nuevaPasswordEntrenador.length < 6 || nuevaPasswordEntrenador !== confirmarPasswordEntrenador}
                      >
                        Guardar Contraseña
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-4">
              {/* Botón de diagnóstico (solo si hay problemas de login) */}
              {entrenadorEditando?.invitacion_aceptada && (
                <Button 
                  variant="outline" 
                  className="w-full border-slate-600 text-slate-400 hover:text-white text-xs"
                  onClick={() => handleDiagnosticarEntrenador(entrenadorEditando)}
                >
                  🔍 Diagnosticar problemas de acceso
                </Button>
              )}
              <div className="flex gap-2">
                <Button onClick={() => { setShowEditarEntrenador(false); setEntrenadorEditando(null); setShowCambiarPassword(false); setNuevaPasswordEntrenador(''); setConfirmarPasswordEntrenador(''); }} variant="outline" className="flex-1">Cancelar</Button>
                <Button onClick={handleGuardarEntrenador} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Guardar Datos</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Clase - Asistencia y Notas */}
      <Dialog open={showClaseModal} onOpenChange={setShowClaseModal}>
        <DialogContent className="max-w-4xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl">
                  {claseActiva?.categoria?.nombre || 'Clase'}
                </DialogTitle>
                <p className="text-sm text-slate-400 mt-1">
                  {claseActiva?.fecha} • {claseActiva?.hora_inicio} - {claseActiva?.hora_fin || 'En curso'}
                </p>
              </div>
              <Badge className={claseActiva?.estado === 'abierta' ? 'bg-emerald-600' : 'bg-slate-600'}>
                {claseActiva?.estado === 'abierta' ? 'En Curso' : 'Cerrada'}
              </Badge>
            </div>
          </DialogHeader>

          {claseActiva && (
            <div className="space-y-6 mt-4">
              {/* Asistencia */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-emerald-400" />
                    Asistencia ({Object.values(asistenciasTemp).filter(v => v).length}/{claseActiva.alumnos?.length || 0})
                  </h3>
                  {claseActiva.estado === 'abierta' && (
                    <Button onClick={handleGuardarAsistencia} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" /> Guardar
                    </Button>
                  )}
                </div>
                
                <div className="bg-slate-900/50 rounded-lg overflow-hidden">
                  {claseActiva.alumnos?.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No hay alumnos en esta categoría</p>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-slate-300">Alumno</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-slate-300">Estado Pago</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-slate-300">Presente</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {claseActiva.alumnos?.map(alumno => (
                          <tr key={alumno.id} className="hover:bg-slate-700/20">
                            <td className="px-4 py-2">
                              <div>
                                <p className="text-white">{alumno.nombre} {alumno.apellido}</p>
                                {alumno.apoderado && (
                                  <p className="text-xs text-slate-500">
                                    Apoderado: {alumno.apoderado.nombre} - {alumno.apoderado.telefono}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              {alumno.estado_pago === 'moroso' ? (
                                <div className="flex items-center gap-1 text-red-400">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="text-xs font-medium">Moroso</span>
                                </div>
                              ) : alumno.estado_pago === 'pendiente' ? (
                                <div className="flex items-center gap-1 text-amber-400">
                                  <AlertTriangle className="w-4 h-4" />
                                  <span className="text-xs font-medium">Pendiente</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-emerald-400">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Al día</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {claseActiva.estado === 'abierta' ? (
                                <button
                                  onClick={() => setAsistenciasTemp({ ...asistenciasTemp, [alumno.id]: !asistenciasTemp[alumno.id] })}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                    asistenciasTemp[alumno.id] 
                                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                                      : 'bg-slate-600 hover:bg-slate-500'
                                  }`}
                                >
                                  {asistenciasTemp[alumno.id] ? (
                                    <Check className="w-5 h-5 text-white" />
                                  ) : (
                                    <XCircle className="w-5 h-5 text-white" />
                                  )}
                                </button>
                              ) : (
                                <span className={alumno.presente ? 'text-emerald-400' : 'text-red-400'}>
                                  {alumno.presente ? '✓' : '✗'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              
              {/* Notas */}
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-amber-400" />
                  Notas de la Clase
                </h3>
                
                {claseActiva.estado === 'abierta' && (
                  <div className="flex gap-2 mb-3">
                    <Textarea
                      value={notaTemp}
                      onChange={(e) => setNotaTemp(e.target.value)}
                      placeholder="Agregar una nota sobre la clase..."
                      className="bg-slate-700 border-slate-600 text-white"
                      rows={2}
                    />
                    <Button onClick={handleAgregarNota} className="bg-amber-600 hover:bg-amber-700 px-4">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                <div className="space-y-2">
                  {claseActiva.notas?.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-2">Sin notas aún</p>
                  ) : (
                    claseActiva.notas?.map(nota => (
                      <div key={nota.id} className="bg-slate-700/30 p-3 rounded-lg">
                        <p className="text-slate-300 text-sm">{nota.contenido}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(nota.created_at).toLocaleString('es-CL')}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Acciones */}
              <div className="flex gap-2 pt-4 border-t border-slate-700">
                <Button onClick={() => setShowClaseModal(false)} variant="outline" className="flex-1">
                  Cerrar
                </Button>
                {claseActiva.estado === 'abierta' && (
                  <Button onClick={handleCerrarClase} className="flex-1 bg-red-600 hover:bg-red-700">
                    <Square className="w-4 h-4 mr-2" /> Finalizar Clase
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Upgrade a Pro */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Actualizar a Plan Pro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-900/30 rounded-lg border border-emerald-700">
              <p className="text-emerald-300 font-medium">Plan Pro - $20 USD/mes</p>
              <ul className="text-sm text-slate-400 mt-2 space-y-1">
                <li>✓ Categorías ilimitadas</li>
                <li>✓ Alumnos ilimitados</li>
                <li>✓ Entrenadores ilimitados</li>
                <li>✓ Módulos de Finanzas y Contabilidad</li>
                <li>✓ Soporte prioritario</li>
              </ul>
            </div>
            
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={upgradeForm.nombre}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, nombre: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={upgradeForm.email}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Teléfono</Label>
              <Input
                value={upgradeForm.telefono}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, telefono: e.target.value })}
                placeholder="+56 9 XXXX XXXX"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label className="text-slate-300">Mensaje (opcional)</Label>
              <Textarea
                value={upgradeForm.mensaje}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, mensaje: e.target.value })}
                placeholder="¿Tienes alguna consulta adicional?"
                className="bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            
            <p className="text-xs text-slate-500">
              Academia: <span className="text-slate-400">{academiaActual?.nombre}</span>
            </p>
            
            <Button 
              onClick={handleEnviarUpgrade} 
              disabled={enviandoUpgrade}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500"
            >
              {enviandoUpgrade ? (
                <>Enviando...</>
              ) : (
                <>Solicitar Upgrade</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Suspender Alumno */}
      <Dialog open={showSuspenderAlumno} onOpenChange={setShowSuspenderAlumno}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Suspender Alumno</DialogTitle>
          </DialogHeader>
          {alumnoASuspender && (
            <div className="space-y-4">
              <p className="text-slate-300">
                ¿Suspender a <span className="font-semibold text-white">{alumnoASuspender.nombre} {alumnoASuspender.apellido}</span>?
              </p>
              <p className="text-sm text-slate-400">
                Sus cuotas pendientes no se contarán en la deuda total mientras esté suspendido.
              </p>
              
              <div>
                <Label className="text-slate-300">Motivo de suspensión *</Label>
                <Select value={suspenderAlumnoForm.motivo} onValueChange={(v) => setSuspenderAlumnoForm(prev => ({ ...prev, motivo: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Selecciona un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retiro_voluntario">Retiro voluntario</SelectItem>
                    <SelectItem value="deuda">Por deuda pendiente</SelectItem>
                    <SelectItem value="inasistencia">Por inasistencia</SelectItem>
                    <SelectItem value="otro">Otro motivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">Detalle (opcional)</Label>
                <Input
                  value={suspenderAlumnoForm.detalle}
                  onChange={(e) => setSuspenderAlumnoForm(prev => ({ ...prev, detalle: e.target.value }))}
                  placeholder="Información adicional..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowSuspenderAlumno(false); setSuspenderAlumnoForm({ motivo: '', detalle: '' }); }} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleSuspenderAlumno} className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={!suspenderAlumnoForm.motivo}>
                  Suspender
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Cartola del Alumno */}
      <Dialog open={showCartolaDialog} onOpenChange={setShowCartolaDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              Cartola de {cartolaAlumno?.nombre} {cartolaAlumno?.apellido}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Estado de cuenta y cuotas del alumno
            </DialogDescription>
          </DialogHeader>
          
          {loadingCartola ? (
            <div className="py-12 text-center">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-slate-400">Cargando cartola...</p>
            </div>
          ) : cartolaData ? (
            <div className="space-y-4">
              {/* Resumen */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-xs text-white">Total Cuotas</p>
                  <p className="text-xl font-bold text-sky-400">{formatMoney(cartolaData.total_cuotas || 0)}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-xs text-white">Pagado</p>
                  <p className="text-xl font-bold text-sky-400">{formatMoney(cartolaData.total_pagado || 0)}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-xs text-white">Pendiente</p>
                  <p className="text-xl font-bold text-red-400">{formatMoney(cartolaData.total_pendiente || 0)}</p>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-lg text-center">
                  <p className="text-xs text-white">Vencido</p>
                  <p className="text-xl font-bold text-red-400">{formatMoney(cartolaData.total_vencido || 0)}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 border-b border-slate-700 pb-2">
                <Button 
                  variant={cartolaTab === 'cuotas' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setCartolaTab('cuotas')}
                  className={cartolaTab === 'cuotas' ? 'bg-emerald-600' : 'text-slate-400'}
                >
                  Cuotas ({cartolaData.cuotas?.length || 0})
                </Button>
                <Button 
                  variant={cartolaTab === 'planes' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setCartolaTab('planes')}
                  className={cartolaTab === 'planes' ? 'bg-emerald-600' : 'text-slate-400'}
                >
                  Planes ({cartolaPlanes?.length || 0})
                </Button>
                <Button 
                  variant={cartolaTab === 'pagos' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setCartolaTab('pagos')}
                  className={cartolaTab === 'pagos' ? 'bg-emerald-600' : 'text-slate-400'}
                >
                  Pagos ({cartolaPagos?.length || 0})
                </Button>
              </div>

              {/* Tab: Cuotas */}
              {cartolaTab === 'cuotas' && (
                <div className="space-y-3">
                  {/* Filtro */}
                  <div className="flex items-center gap-2">
                    <Select value={cartolaFiltro} onValueChange={setCartolaFiltro}>
                      <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todas</SelectItem>
                        <SelectItem value="pendiente">Pendientes</SelectItem>
                        <SelectItem value="vencida">Vencidas</SelectItem>
                        <SelectItem value="pagada">Pagadas</SelectItem>
                      </SelectContent>
                    </Select>
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={handleRecalcularCuotasAlumno} className="border-slate-600 ml-auto">
                        <RefreshCw className="w-4 h-4 mr-1" /> Recalcular
                      </Button>
                    )}
                  </div>

                  {/* Lista de cuotas */}
                  {cuotasFiltradas.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">No hay cuotas</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {cuotasFiltradas.map(cuota => (
                        <div key={cuota.id} className="p-3 bg-slate-700/30 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-medium">{cuota.descripcion}</p>
                            <p className="text-xs text-slate-400">
                              Vence: {cuota.fecha_vencimiento ? new Date(cuota.fecha_vencimiento).toLocaleDateString('es-CL') : 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className={`font-bold ${cuota.estado === 'pagada' ? 'text-sky-400' : 'text-red-400'}`}>
                                {formatMoney(cuota.monto_final || cuota.monto)}
                              </p>
                              <Badge className={
                                cuota.estado === 'pagada' ? 'bg-sky-600/30 text-sky-400' :
                                cuota.estado === 'vencida' ? 'bg-red-600/30 text-red-400' :
                                'bg-amber-600/30 text-amber-400'
                              }>
                                {cuota.estado === 'pagada' ? 'Pagada' : cuota.estado === 'vencida' ? 'Vencida' : 'Pendiente'}
                              </Badge>
                            </div>
                            {canEdit && cuota.estado !== 'pagada' && (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleCondonarCuotaAlumno(cuota.id)} title="Condonar" className="text-slate-400 hover:text-green-400 h-8 w-8">
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleAnularCuotaAlumno(cuota.id)} title="Anular" className="text-slate-400 hover:text-red-400 h-8 w-8">
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Planes */}
              {cartolaTab === 'planes' && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cartolaPlanes.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">Sin planes asignados</p>
                  ) : cartolaPlanes.map(plan => (
                    <div key={plan.id} className="p-3 bg-slate-700/30 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{plan.nombre}</p>
                        <p className="text-xs text-slate-400">{formatMoney(plan.monto)} / {plan.periodicidad}</p>
                      </div>
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => handleQuitarPlanAlumno(plan.id)} className="text-slate-400 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Tab: Pagos */}
              {cartolaTab === 'pagos' && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cartolaPagos.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">Sin pagos registrados</p>
                  ) : cartolaPagos.map(pago => (
                    <div key={pago.id} className="p-3 bg-slate-700/30 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{pago.descripcion || 'Pago de cuota'}</p>
                        <p className="text-xs text-slate-400">{new Date(pago.fecha_pago).toLocaleDateString('es-CL')}</p>
                      </div>
                      <p className="text-sky-400 font-bold">{formatMoney(pago.monto)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">No se pudo cargar la información</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
